import {
  Activity,
  ArrowRight,
  BadgeDollarSign,
  Bell,
  Building2,
  CreditCard,
  FileText,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Menu,
  ReceiptText,
  UserCircle,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getClientBillingDashboard,
  createClientAccountPlacement,
  type ARClientBillingDashboard,
  getCanonicalARClientPortal,
  type ARClientPortalEvent,
  type ARClientPortalPayload,
} from "../../lib/arRecoveryClientPortalApi";
import { createBillingPayNowUrl } from "../../lib/arRecoveryBillingApi";
import { logout } from "../../lib/api";
import { clearDemoAuthRole } from "../../lib/runtimeMode";
import { ARRecoveryClientPaymentReporting } from "../forms/ARRecoveryClientPaymentReporting";

type PortalWorkspace =
  | "overview"
  | "accounts"
  | "recovery"
  | "customer-payments"
  | "billing"
  | "reports"
  | "organization"
  | "support"
  | "profile";

type EnterpriseWindowTarget =
  | "report-payment"
  | "recovery"
  | "customer-payments"
  | "statements"
  | "billing"
  | "pay-now"
  | "receipt"
  | "payment-activity"
  | "billing-history"
  | "support";

const primaryWorkspaces: Array<{
  key: Exclude<PortalWorkspace, "support" | "profile" | "organization">;
  label: string;
  description: string;
  route: string;
  icon: typeof LayoutDashboard;
  disabled?: boolean;
}> = [
  { key: "overview", label: "Home", description: "Available when actionable work is certified", route: "overview", icon: LayoutDashboard, disabled: true },
  { key: "accounts", label: "Accounts", description: "Customer account work", route: "accounts", icon: Building2 },
  { key: "customer-payments", label: "Customer Payments", description: "Payments received from your customers", route: "payments", icon: CreditCard },
  { key: "billing", label: "Billing", description: "Invoices, receipts, and history", route: "billing", icon: BadgeDollarSign },
  { key: "reports", label: "Reports", description: "Statements, downloads, and documents", route: "reports", icon: FileText },
];

const utilityNavigation: Array<{ key: Extract<PortalWorkspace, "organization" | "support" | "profile">; label: string; route: string; icon: typeof LifeBuoy }> = [
  { key: "organization", label: "Organization", route: "organization", icon: Building2 },
  { key: "support", label: "Help and Support", route: "support", icon: LifeBuoy },
  { key: "profile", label: "Profile", route: "profile", icon: UserCircle },
];

export function CollabRecoverClientPortalPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const workspaceRegionRef = useRef<HTMLDivElement>(null);
  const workspaceSelectorTriggerRef = useRef<HTMLButtonElement>(null);
  const [portal, setPortal] = useState<ARClientPortalPayload | null>(null);
  const [portalRefreshKey, setPortalRefreshKey] = useState(0);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [workspaceSelectorOpen, setWorkspaceSelectorOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const selectedAccountRouteId = accountIdFromPath(location.pathname);
  const portalQuery = useMemo(() => {
    const requestedPageSize = Number(new URLSearchParams(location.search).get("pageSize") || 25);
    const pageSize = ([25, 50, 100] as const).includes(requestedPageSize as 25 | 50 | 100)
      ? requestedPageSize as 25 | 50 | 100
      : 25;
    return {
      page: Number(new URLSearchParams(location.search).get("accountPage") || 1),
      pageSize,
      accountId: selectedAccountRouteId,
      accountSearch: new URLSearchParams(location.search).get("accountSearch") || "",
      accountStatus: new URLSearchParams(location.search).get("accountStatus") || "",
      accountSort: new URLSearchParams(location.search).get("accountSort") === "balance" ? "balance" as const : "customer" as const,
    };
  }, [location.pathname, location.search, selectedAccountRouteId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getCanonicalARClientPortal(undefined, portalQuery)
      .then((payload) => {
        if (active) setPortal(payload);
      })
      .catch((loadError) => {
        if (active) setError(loadError instanceof Error ? loadError.message : "CollabRecover Client Portal could not be loaded.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [portalQuery, portalRefreshKey]);

  useEffect(() => {
    document.title = "CollabRecover Client Portal | CollabRecover AR Solutions";
    document.querySelector('meta[name="description"]')?.setAttribute(
      "content",
      "Client-facing AR recovery portal for accounts, billing invoices, payments, receipts, statements, and recovery activity.",
    );
  }, []);

  useEffect(() => {
    workspaceRegionRef.current?.focus({ preventScroll: true });
  }, [location.pathname]);

  const activeWorkspace = useMemo(() => workspaceFromPath(location.pathname), [location.pathname]);
  const isSelectedAccountWorkspace = activeWorkspace === "accounts" && Boolean(selectedAccountRouteId);
  const isAccountsListWorkspace = activeWorkspace === "accounts" && !isSelectedAccountWorkspace;
  const activeAction = useMemo(() => actionFromPath(location.pathname), [location.pathname]);
  const activeWorkspaceMeta = [...primaryWorkspaces, ...utilityNavigation].find((workspace) => workspace.key === activeWorkspace);
  const selectedAccount = portal?.accounts.find((account) => account.accountId === selectedAccountId)
    || portal?.accounts[0]
    || null;
  const routeInvoiceId = invoiceIdFromPath(location.pathname) || enterpriseWindowInvoiceId(location.search);
  const isInvoiceListRoute = billingInvoiceListFromPath(location.pathname);
  const selectedInvoice = portal?.billingCenter.invoices.find((invoice) => invoice.billingInvoiceId === routeInvoiceId) || null;
  const enterpriseWindowTriggerRef = useRef<HTMLElement | null>(null);
  const activeEnterpriseWindow = enterpriseWindowTargetFromSearch(location.search) || (activeAction === "report-payment" ? "report-payment" : null);
  const hasActiveTask = activeEnterpriseWindow === "report-payment" || activeEnterpriseWindow === "pay-now";
  const showTaskContext = hasActiveTask && activeEnterpriseWindow !== "report-payment";
  useEffect(() => {
    const routeAccountId = accountIdFromPath(location.pathname) || enterpriseWindowAccountId(location.search);
    if (routeAccountId && portal?.accounts.some((account) => account.accountId === routeAccountId)) {
      setSelectedAccountId(routeAccountId);
      return;
    }
    if (!selectedAccountId && portal?.accounts[0]) {
      setSelectedAccountId(portal.accounts[0].accountId);
    }
  }, [location.pathname, location.search, portal, selectedAccountId]);

  useEffect(() => {
    if (location.pathname !== "/client/portal/recovery" && location.pathname !== "/client/portal/activity") return;
    const accountId = selectedAccountId || portal?.accounts[0]?.accountId;
    navigate(accountId ? `/client/portal/accounts/${encodeURIComponent(accountId)}?workspaceWindow=recovery` : "/client/portal/accounts", { replace: true });
  }, [location.pathname, navigate, portal, selectedAccountId]);

  const openWorkspace = (workspace: PortalWorkspace, pathSuffix?: string) => {
    const route = pathSuffix || routeForWorkspace(workspace);
    setWorkspaceSelectorOpen(false);
    navigate(`/client/portal/${route}`);
  };

  const updateAccountDirectory = (updates: Partial<typeof portalQuery>) => {
    const params = new URLSearchParams(location.search);
    const next = { ...portalQuery, ...updates };
    const mapped: Array<[keyof typeof portalQuery, string]> = [
      ["page", "accountPage"],
      ["pageSize", "pageSize"],
      ["accountSearch", "accountSearch"],
      ["accountStatus", "accountStatus"],
      ["accountSort", "accountSort"],
    ];
    for (const [key, param] of mapped) {
      const value = next[key];
      if (!value || value === "customer" || value === 1) params.delete(param);
      else params.set(param, String(value));
    }
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : "" }, { replace: true });
  };

  const closeWorkspaceSelector = () => {
    setWorkspaceSelectorOpen(false);
    window.requestAnimationFrame(() => workspaceSelectorTriggerRef.current?.focus({ preventScroll: true }));
  };

  const openReportPaymentTask = () => {
    launchEnterpriseWindow("report-payment");
  };

  const openAccountsDirectory = () => {
    const params = new URLSearchParams(location.search);
    params.delete("workspaceWindow");
    params.delete("workspaceAccountId");
    params.delete("workspaceInvoiceId");
    params.delete("returnTo");
    navigate({ pathname: "/client/portal/accounts", search: params.toString() ? `?${params}` : "" });
  };

  const selectAccount = (accountId: string) => {
    setSelectedAccountId(accountId);
    const params = new URLSearchParams(location.search);
    params.delete("workspaceWindow");
    params.delete("workspaceAccountId");
    params.delete("workspaceInvoiceId");
    params.delete("returnTo");
    navigate(`/client/portal/accounts/${encodeURIComponent(accountId)}${params.toString() ? `?${params}` : ""}`);
  };

  const closeReportPaymentTask = () => {
    navigate(enterpriseWindowOriginPath(location.pathname, location.search), { replace: true });
    window.requestAnimationFrame(() => enterpriseWindowTriggerRef.current?.focus({ preventScroll: true }));
  };

  const launchEnterpriseWindow = (target: EnterpriseWindowTarget, trigger?: HTMLElement | null) => {
    enterpriseWindowTriggerRef.current = trigger || null;
    navigate(enterpriseWindowRoute({
      target,
      pathname: location.pathname,
      search: location.search,
      accountId: selectedAccount?.accountId || "",
      invoiceId: selectedInvoice?.billingInvoiceId || "",
    }));
  };

  useEffect(() => {
    if (!activeEnterpriseWindow) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeReportPaymentTask();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeEnterpriseWindow, location.search]);

  useEffect(() => {
    if (!workspaceSelectorOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeWorkspaceSelector();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [workspaceSelectorOpen]);

  const activeWorkspaceContent = !portal ? null : activeWorkspace === "accounts"
      ? <AccountsTab
          portal={portal}
          selectedAccountId={selectedAccount?.accountId || ""}
          selectedAccountWorkspace={isSelectedAccountWorkspace}
          selectedInvoice={selectedInvoice}
          activeAction={activeEnterpriseWindow}
          directoryQuery={portalQuery}
          onSelectAccount={selectAccount}
          onDirectoryQueryChange={updateAccountDirectory}
          onNavigate={openWorkspace}
          onBackToAccounts={openAccountsDirectory}
          onLaunch={launchEnterpriseWindow}
          onSubmitted={() => { setPortalRefreshKey((value) => value + 1); closeReportPaymentTask(); }}
        />
      : activeWorkspace === "recovery"
        ? <ActivityTab portal={portal} selectedAccountId={selectedAccount?.accountId} />
        : activeWorkspace === "customer-payments"
          ? activeEnterpriseWindow === "report-payment"
            ? <ClientPortalPaymentReporting portal={portal} selectedAccount={isSelectedAccountWorkspace ? selectedAccount : null} onSubmitted={() => { setPortalRefreshKey((value) => value + 1); closeReportPaymentTask(); }} />
            : <PaymentsTab portal={portal} />
          : activeWorkspace === "billing"
            ? <BillingTab portal={portal} billingPath={location.pathname} selectedInvoiceId={routeInvoiceId} isInvoiceListRoute={isInvoiceListRoute} paymentReturnStatus={new URLSearchParams(location.search).get("payment")} onNavigate={openWorkspace} />
            : activeWorkspace === "reports"
              ? <ReportsWorkspace portal={portal} onNavigate={openWorkspace} />
              : activeWorkspace === "organization"
                ? <OrganizationTab portal={portal} />
                : activeWorkspace === "support"
                ? <SupportTab portal={portal} />
                : activeWorkspace === "profile"
                  ? <ProfileTab portal={portal} />
                  : <OverviewTab portal={portal} onNavigate={openWorkspace} onReportPayment={openReportPaymentTask} />;

  const onLogout = async () => {
    try {
      await logout();
    } finally {
      clearDemoAuthRole();
      navigate("/login");
    }
  };

  return (
    <main className="min-h-screen bg-[#F3F8FA] text-[#0A2540]">
      <header className="sticky top-0 z-30 border-b border-[#CFE3EA] bg-white px-4 py-2.5 shadow-[0_6px_18px_rgba(10,37,64,.07)] sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <span className="grid size-[39px] shrink-0 place-items-center shadow-[0_12px_30px_rgba(14,165,233,.24)]">
              <img
                src="/assets/collabrecover-cr-logo.png"
                alt=""
                className="h-full w-full object-contain"
              />
            </span>
            <div>
              <p className="text-xs font-black uppercase tracking-[0.22em] max-[359px]:tracking-[0.08em] text-[#14B8A6]">CollabRecover AR Solutions</p>
              <h1 className="text-2xl font-black tracking-[-0.04em] text-[#071A2F]">Client Portal</h1>
            </div>
          </div>
        </div>
      </header>

      <section className="mx-auto grid max-w-[90rem] gap-3 px-4 py-3 sm:px-6 sm:py-4 lg:px-8">
        {loading && !portal ? (
          <PortalFrame>
            <p className="text-sm font-bold text-[#475569]">Loading CollabRecover client records...</p>
          </PortalFrame>
        ) : error && !portal ? (
          <PortalFrame>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#DC2626]">Access unavailable</p>
            <h2 className="mt-2 text-2xl font-black">CollabRecover Client Portal</h2>
            <p className="mt-2 text-sm font-semibold text-[#475569]">{error}</p>
          </PortalFrame>
        ) : portal ? (
          <>
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[#CFE3EA] bg-white px-3 py-2 text-xs sm:px-6 lg:px-8" aria-label="Client organization context">
              <p className="min-w-0 font-bold text-[#475569]"><span className="font-black uppercase tracking-[0.14em] text-[#0F766E]">Organization</span> <span className="break-words">{portal.clients[0]?.displayName || "Authorized AR Client"}</span></p>
              <p className="shrink-0 font-bold text-[#475569]"><span className="font-black uppercase tracking-[0.14em] text-[#0F766E]">Role</span> {portal.membership.permissionProfile}</p>
            </header>

             <section className="border border-[#CFE3EA] bg-white shadow-[0_12px_36px_rgba(10,37,64,.06)]" aria-label="Client Portal workspace shell">
              <div className="grid gap-0 lg:grid-cols-[minmax(15rem,17rem)_minmax(0,1fr)]">
                <div className="min-w-0 lg:hidden">
                  <button
                    ref={workspaceSelectorTriggerRef}
                    type="button"
                    aria-controls="client-portal-mobile-workspaces"
                    aria-expanded={workspaceSelectorOpen}
                    aria-label="Select Client Portal workspace"
                    onClick={() => setWorkspaceSelectorOpen((open) => !open)}
                    className="flex w-full min-h-12 items-center justify-between gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F8FDFF] px-4 py-3 text-left shadow-[0_8px_20px_rgba(10,37,64,.04)] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
                  >
                    <span className="min-w-0">
                      <span className="block text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">Active workspace</span>
                      <span className="mt-0.5 block truncate text-sm font-black text-[#071A2F]">{activeWorkspaceMeta?.label || "Overview"}</span>
                    </span>
                    <Menu className="size-5 shrink-0 text-[#0F766E]" aria-hidden="true" />
                  </button>
                  {workspaceSelectorOpen ? (
                    <nav id="client-portal-mobile-workspaces" className="mt-2 rounded-2xl border border-[#BFEAFF] bg-white p-3 shadow-[0_18px_40px_rgba(10,37,64,.13)]" aria-label="Client Portal workspace selector">
                      <p className="px-2 text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">Workspaces</p>
                      <div className="mt-2 grid gap-2">
                        {primaryWorkspaces.map((workspace) => {
                          const Icon = workspace.icon;
                          const selected = activeWorkspace === workspace.key;
                           return (
                             <button key={workspace.key} type="button" disabled={workspace.disabled} aria-current={selected ? "page" : undefined} onClick={() => { if (!workspace.disabled) openWorkspace(workspace.key); }} className={`flex min-h-11 w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left focus:outline-none focus:ring-2 focus:ring-[#14B8A6] ${workspace.disabled ? "cursor-not-allowed border-transparent text-[#94A3B8]" : selected ? "border-[#14B8A6] bg-[#E8FFFB] text-[#0F766E]" : "border-transparent text-[#0A2540] hover:border-[#BFEAFF] hover:bg-[#F8FDFF]"}`}>
                              <Icon className="size-4 shrink-0" aria-hidden="true" />
                              <span className="min-w-0 truncate text-sm font-black">{workspace.label}</span>
                            </button>
                          );
                        })}
                      </div>
                      <button type="button" onClick={() => { setWorkspaceSelectorOpen(false); openReportPaymentTask(); }} className={`mt-3 flex min-h-11 w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm font-black focus:outline-none focus:ring-2 focus:ring-[#14B8A6] ${activeEnterpriseWindow === "report-payment" ? "border-[#14B8A6] bg-[#0A2540] text-white shadow-[0_8px_18px_rgba(10,37,64,.16)]" : "border-[#BFEAFF] bg-white text-[#0A2540] hover:border-[#38BDF8]"}`} aria-label="Report a Customer Payment">
                        <ReceiptText className="size-4 shrink-0 text-[#5EEAD4]" aria-hidden="true" />
                        <span>Report a Customer Payment</span>
                      </button>
                      <div className="mt-3 border-t border-[#D8F3FF] pt-3">
                        <p className="px-2 text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">Utilities</p>
                        <div className="mt-2 grid gap-2">
                          {utilityNavigation.map((item) => {
                            const Icon = item.icon;
                            const selected = activeWorkspace === item.key;
                            return <button key={item.key} type="button" aria-current={selected ? "page" : undefined} onClick={() => openWorkspace(item.key)} className={`flex min-h-11 w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm font-black focus:outline-none focus:ring-2 focus:ring-[#14B8A6] ${selected ? "border-[#14B8A6] bg-[#E8FFFB] text-[#0F766E]" : "border-[#BFEAFF] bg-white text-[#0A2540]"}`}><Icon className="size-4 shrink-0" aria-hidden="true" />{item.label}</button>;
                          })}
                          <button type="button" onClick={() => void onLogout()} className="flex min-h-11 w-full items-center gap-3 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2.5 text-left text-sm font-black text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"><LogOut className="size-4 shrink-0" aria-hidden="true" />Sign Out</button>
                        </div>
                      </div>
                    </nav>
                  ) : null}
                </div>
                <nav className="hidden min-w-0 border-r border-[#D8E6EB] bg-[#F7FAFB] p-3 lg:block" aria-label="Primary workspace navigation">
                  <div className="flex items-center gap-2 px-2 py-2 text-xs font-black uppercase tracking-[0.18em] text-[#0F766E]">
                    <Menu className="size-4" aria-hidden="true" />
                    Workspaces
                  </div>
                  <div className="grid gap-2">
                    {primaryWorkspaces.map((workspace) => {
                      const Icon = workspace.icon;
                      const selected = activeWorkspace === workspace.key;
                           return (
                             <button
                               key={workspace.key}
                               type="button"
                               disabled={workspace.disabled}
                               aria-current={selected ? "page" : undefined}
                               onClick={() => { if (!workspace.disabled) openWorkspace(workspace.key); }}
                               className={`group flex w-full min-h-11 items-center gap-3 rounded-lg border px-3 py-2 text-left transition focus:outline-none focus:ring-2 focus:ring-[#14B8A6] ${workspace.disabled ? "cursor-not-allowed border-transparent text-[#94A3B8]" : ""} ${
                                 selected
                                   ? "border-[#9DD8D1] bg-[#EAF8F5] text-[#071A2F] shadow-[0_4px_10px_rgba(10,37,64,.05)]"
                                   : "border-transparent text-[#475569] hover:border-[#CFE3EA] hover:bg-white"
                               }`}
                        >
                          <Icon className={`size-4 shrink-0 ${selected ? "text-[#0F766E]" : "text-[#38BDF8]"}`} aria-hidden="true" />
                          <span className="min-w-0">
                            <span className="block text-sm font-black">{workspace.label}</span>
                            <span className="block break-words text-xs font-semibold leading-4">{workspace.description}</span>
                          </span>
                        </button>
                             );
                       })}
                  </div>
                  <button type="button" onClick={openReportPaymentTask} className={`mt-3 flex min-h-11 w-full items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm font-black focus:outline-none focus:ring-2 focus:ring-[#14B8A6] ${activeEnterpriseWindow === "report-payment" ? "border-[#14B8A6] bg-[#0A2540] text-white shadow-[0_8px_18px_rgba(10,37,64,.16)]" : "border-[#D8E6EB] bg-white text-[#0A2540] hover:border-[#38BDF8]"}`} aria-label="Report a Customer Payment">
                    <ReceiptText className="size-4 shrink-0 text-[#5EEAD4]" aria-hidden="true" />
                    <span>Report a Customer Payment</span>
                  </button>
                  <div className="mt-3 border-t border-[#D8E6EB] pt-3">
                    <p className="px-2 text-xs font-black uppercase tracking-[0.18em] text-[#0F766E]">Utilities</p>
                    <div className="mt-2 grid gap-2">
                      {utilityNavigation.map((item) => {
                        const Icon = item.icon;
                        const selected = activeWorkspace === item.key;
                        return (
                          <button
                            key={item.key}
                            type="button"
                            aria-current={selected ? "page" : undefined}
                            onClick={() => openWorkspace(item.key)}
                            className={`inline-flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm font-extrabold transition focus:outline-none focus:ring-2 focus:ring-[#14B8A6] ${selected ? "border-[#14B8A6] bg-[#E8FFFB] text-[#0F766E]" : "border-[#D8E6EB] bg-white text-[#0A2540] hover:border-[#38BDF8]"}`}
                          >
                            <Icon className="size-4" aria-hidden="true" />
                            {item.label}
                          </button>
                        );
                      })}
                      <button type="button" onClick={() => void onLogout()} className="inline-flex min-h-11 w-full items-center gap-2 rounded-lg border border-[#D8E6EB] bg-white px-3 py-2 text-left text-sm font-extrabold text-[#0A2540] transition hover:border-[#38BDF8] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"><LogOut className="size-4" aria-hidden="true" />Sign Out</button>
                    </div>
                  </div>
                </nav>

                <div className="min-w-0 bg-white p-3 sm:p-4" aria-live="polite">
                  <WorkspaceHeader
                    title={activeWorkspaceMeta?.label || "Overview"}
                    description={workspacePurpose(activeWorkspace)}
                    action={
                      activeWorkspace === "accounts" || activeWorkspace === "customer-payments" || activeWorkspace === "billing" || activeWorkspace === "organization" || activeWorkspace === "profile" || activeWorkspace === "support" ? null : <EnterpriseActionBar
                        activeTask={hasActiveTask}
                        selectedAccount={selectedAccount}
                        selectedInvoice={selectedInvoice}
                        onReportPayment={openReportPaymentTask}
                        onNavigate={openWorkspace}
                        neutralReportAction={activeWorkspace === "recovery"}
                      />
                    }
                  />

                  {selectedAccount && activeWorkspace !== "overview" && activeWorkspace !== "accounts" && activeWorkspace !== "customer-payments" && activeWorkspace !== "billing" && activeWorkspace !== "organization" && activeWorkspace !== "profile" && activeWorkspace !== "support" ? (
                    <SelectedAccountContext account={selectedAccount} onNavigate={openWorkspace} />
                  ) : null}

                  <div ref={workspaceRegionRef} className={`mt-3 focus:outline-none ${hasActiveTask ? "pb-24" : ""}`} id="client-portal-workspace" tabIndex={-1}>
                    {loading ? <div className="rounded-xl border border-[#BFEAFF] bg-[#F8FDFF] p-6 text-sm font-bold text-[#475569]" role="status" aria-live="polite">Loading CollabRecover client records...</div> : null}
                    {error ? <div className="rounded-xl border border-[#FECACA] bg-[#FFF7F7] p-6 text-sm font-semibold text-[#991B1B]" role="alert">{error}</div> : null}
                    {!loading ? <>
                      <div className={isAccountsListWorkspace ? "" : showTaskContext ? "grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(15rem,.3fr)]" : ""}>
                        <section className="min-w-0 rounded-xl border border-[#CFE3EA] bg-white p-3 shadow-[0_10px_28px_rgba(10,37,64,.06)] sm:p-4" aria-label="Execution Workspace">
                          {activeWorkspaceContent}
                        </section>
                        {showTaskContext ? <ContextWorkspace
                          portal={portal}
                          selectedInvoice={selectedInvoice}
                          activeTask={hasActiveTask}
                          onNavigate={activeWorkspace === "accounts"
                            ? (workspace) => launchEnterpriseWindow(enterpriseWindowTargetForWorkspace(workspace))
                            : openWorkspace}
                        /> : null}
                      </div>
                       {hasActiveTask ? <footer className="fixed inset-x-0 bottom-0 z-40 flex flex-wrap items-center justify-between gap-3 border-t border-[#CFE3EA] bg-[#F7FAFB] px-4 py-3 shadow-[0_-10px_28px_rgba(10,37,64,.16)]" aria-label="Footer actions">
                         <p className="text-xs font-semibold text-[#475569]">Complete or cancel this task to return to {taskOriginLabel(location.search)}.</p>
                         <button type="button" onClick={closeReportPaymentTask} className="min-h-11 rounded-full border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-black text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">Cancel and return to {taskOriginLabel(location.search)}</button>
                       </footer> : null}
                    </> : null}
                  </div>
                </div>
              </div>
            </section>
          </>
        ) : null}
      </section>
    </main>
  );
}

function OverviewTab({
  portal,
  onNavigate,
  onReportPayment,
}: {
  portal: ARClientPortalPayload;
  onNavigate: (workspace: PortalWorkspace, pathSuffix?: string) => void;
  onReportPayment: () => void;
}) {
  const attention = portal.notifications.slice(0, 4);
  const latestSettlement = portal.settlementBilling.invoices[0] || null;
  const nextInvoice = portal.billingCenter.invoices.find((invoice) => invoice.remainingBalanceCents > 0) || portal.billingCenter.invoices[0] || null;
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_.8fr]">
      <PortalCard title="Attention Required" eyebrow="Client action">
        <div className="grid gap-3">
          {attention.map((notice) => (
            <RecordRow key={notice.id} title={notice.title} detail={notice.detail} meta={notice.type} />
          ))}
          {!attention.length ? <EmptyState message="No client action is required right now." /> : null}
        </div>
      </PortalCard>
      <PortalCard title="Active Recovery Summary" eyebrow="Accounts receivable">
        <div className="grid gap-3">
          {portal.accounts.slice(0, 5).map((account) => (
            <RecordRow
              key={account.accountId}
              title={`${account.customerName} / ${account.accountNumber}`}
              detail={`${account.status} / ${account.placementStatus}`}
              meta={formatMoney(account.currentBalanceCents)}
              onAction={() => onNavigate("accounts", `accounts/${encodeURIComponent(account.accountId)}`)}
              actionLabel="View related account"
            />
          ))}
          {!portal.accounts.length ? <EmptyState message="No client-visible accounts are available." actionLabel="Contact support" onAction={() => onNavigate("support")} /> : null}
        </div>
      </PortalCard>
      <PortalCard title="Latest Settlement" eyebrow="Settlement visibility">
        {latestSettlement ? (
          <RecordRow
            title={latestSettlement.settlementNumber}
            detail={`${latestSettlement.invoiceStatus} / ${latestSettlement.paymentStatus} / due ${formatDate(latestSettlement.dueDate)}`}
            meta={formatMoney(latestSettlement.amountDueCents)}
            onAction={() => onNavigate("billing", "billing?view=settlements")}
            actionLabel="View settlement"
          />
        ) : (
          <EmptyState message="No client-visible settlement invoice is available." />
        )}
      </PortalCard>
      <PortalCard title="Quick Actions" eyebrow="Next step">
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            ["Report a Customer Payment", "Submit information about money received from a customer without leaving this workspace.", "report-payment"],
            ["View Accounts", "Review account balances, statuses, and recent account updates.", "accounts"],
            ["View Recovery Activity", "Open the client-safe recovery activity timeline.", "recovery"],
            ["View Invoices", nextInvoice ? `Open invoice ${nextInvoice.invoiceNumber}.` : "Review open CollabRecover billing invoices and receipts.", "billing"],
          ].map(([label, detail, target]) => (
            <button
              key={label}
              type="button"
              onClick={() => target === "report-payment" ? onReportPayment() : onNavigate(target as PortalWorkspace)}
              className="rounded-2xl border border-[#D8F3FF] bg-[#F8FDFF] p-4 text-left transition hover:border-[#38BDF8] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
            >
              <span className="text-sm font-black text-[#071A2F]">{label}</span>
              <span className="mt-1 block text-xs font-semibold leading-5 text-[#64748B]">{detail}</span>
            </button>
          ))}
        </div>
      </PortalCard>
    </div>
  );
}

function AccountsTab({
  portal,
  selectedAccountId,
  selectedAccountWorkspace,
  selectedInvoice,
  activeAction,
  directoryQuery,
  onSelectAccount,
  onDirectoryQueryChange,
  onNavigate,
  onBackToAccounts,
  onLaunch,
  onSubmitted,
}: {
  portal: ARClientPortalPayload;
  selectedAccountId: string;
  selectedAccountWorkspace: boolean;
  selectedInvoice: ARClientPortalPayload["billingCenter"]["invoices"][number] | null;
  activeAction: EnterpriseWindowTarget | null;
  directoryQuery: { page: number; pageSize: 25 | 50 | 100; accountSearch: string; accountStatus: string; accountSort: "customer" | "balance" };
  onSelectAccount: (accountId: string) => void;
  onDirectoryQueryChange: (updates: Partial<{ page: number; pageSize: 25 | 50 | 100; accountSearch: string; accountStatus: string; accountSort: "customer" | "balance" }>) => void;
  onNavigate: (workspace: PortalWorkspace, pathSuffix?: string) => void;
  onBackToAccounts: () => void;
  onLaunch: (target: EnterpriseWindowTarget, trigger?: HTMLElement | null) => void;
  onSubmitted: () => void;
}) {
  const [accountSearch, setAccountSearch] = useState(directoryQuery.accountSearch);
  const [placementMode, setPlacementMode] = useState<"manual" | "upload" | null>(null);
  const queueRef = useRef<HTMLDivElement>(null);
  const queueScrollTopRef = useRef(0);
  const selectedAccount = portal.accounts.find((account) => account.accountId === selectedAccountId) || portal.accounts[0] || null;
  const filteredAccounts = portal.accounts;
  const selectedAccountPayments = selectedAccount ? portal.payments.filter((payment) => payment.accountId === selectedAccount.accountId).slice(0, 3) : [];
  const selectedAccountStatements = selectedAccount ? portal.statements.filter((statement) => statement.accountId === selectedAccount.accountId).slice(0, 3) : [];
  const selectedAccountDocuments = selectedAccount ? portal.documents.filter((document) => document.accountId === selectedAccount.accountId).slice(0, 3) : [];
  const selectedAccountLastActivity = selectedAccount
    ? portal.activity.find((event) => event.accountId === selectedAccount.accountId)?.occurredAt || selectedAccount.updatedAt
    : null;

  useEffect(() => {
    setAccountSearch(directoryQuery.accountSearch);
  }, [directoryQuery.accountSearch]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (accountSearch !== directoryQuery.accountSearch) onDirectoryQueryChange({ accountSearch, page: 1 });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [accountSearch, directoryQuery.accountSearch, onDirectoryQueryChange]);

  const launchForAccount = (target: EnterpriseWindowTarget, trigger?: HTMLElement | null) => {
    onLaunch(target, trigger);
  };

  useEffect(() => {
    if (!selectedAccountWorkspace && queueRef.current) queueRef.current.scrollTop = queueScrollTopRef.current;
  }, [selectedAccountWorkspace]);

  if (!selectedAccountWorkspace) {
    return <section className="min-w-0" aria-label="Accounts directory">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-[#D8E6EB] pb-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#0F766E]">Accounts workspace</p>
          <h2 className="mt-1 text-2xl font-black text-[#071A2F]">Account directory</h2>
          <p className="mt-1 text-sm font-semibold text-[#64748B]">{portal.pagination.accounts.total} authorized customer accounts</p>
        </div>
        <div className="mt-3 grid gap-2">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setPlacementMode("manual")} className="min-h-11 rounded-full bg-[#0A2540] px-4 py-2 text-sm font-black text-white focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">Add an Account</button>
            <button type="button" onClick={() => setPlacementMode("upload")} className="min-h-11 rounded-full border border-[#14B8A6] bg-[#E8FFFB] px-4 py-2 text-sm font-black text-[#0F766E] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">Upload Accounts</button>
          </div>
          <input
            className="w-full rounded-2xl border border-[#BFEAFF] px-3 py-2 text-sm font-bold text-[#071A2F] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
            value={accountSearch}
            onChange={(event) => setAccountSearch(event.target.value)}
            placeholder="Search customer, account, status..."
            aria-label="Search client accounts"
          />
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <select value={directoryQuery.accountStatus || "all"} onChange={(event) => onDirectoryQueryChange({ accountStatus: event.target.value === "all" ? "" : event.target.value, page: 1 })} aria-label="Filter account queue" className="min-h-10 rounded-xl border border-[#BFEAFF] bg-white px-2 text-xs font-black text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">
              <option value="all">All statuses</option>
              {[...new Set(portal.accounts.map((account) => account.status))].map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
            <select value={directoryQuery.accountSort} onChange={(event) => onDirectoryQueryChange({ accountSort: event.target.value as "customer" | "balance", page: 1 })} aria-label="Sort account queue" className="min-h-10 rounded-xl border border-[#BFEAFF] bg-white px-2 text-xs font-black text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">
              <option value="customer">Customer name</option>
              <option value="balance">Balance</option>
            </select>
            <select value={directoryQuery.pageSize} onChange={(event) => onDirectoryQueryChange({ pageSize: Number(event.target.value) as 25 | 50 | 100, page: 1 })} aria-label="Accounts per page" className="min-h-10 rounded-xl border border-[#BFEAFF] bg-white px-2 text-xs font-black text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">
              {[25, 50, 100].map((size) => <option key={size} value={size}>{size} per page</option>)}
            </select>
          </div>
        </div>
      </div>
      {placementMode ? <AccountPlacementPanel portal={portal} mode={placementMode} onClose={() => setPlacementMode(null)} onSubmitted={() => { setPlacementMode(null); onDirectoryQueryChange({ page: directoryQuery.page }); }} /> : null}
        <div ref={queueRef} onScroll={(event) => { queueScrollTopRef.current = event.currentTarget.scrollTop; }} className="mt-4 grid max-h-[36rem] gap-2 overflow-y-auto pr-1" aria-label="Account directory results">
          {filteredAccounts.map((account) => {
            const selected = selectedAccount?.accountId === account.accountId;
            return (
              <button
                key={account.accountId}
                type="button"
                aria-current={selected ? "true" : undefined}
                onClick={() => {
                  queueScrollTopRef.current = queueRef.current?.scrollTop || 0;
                  onSelectAccount(account.accountId);
                  onSelectAccount(account.accountId);
                }}
                className={`rounded-2xl border p-3 text-left transition focus:outline-none focus:ring-2 focus:ring-[#14B8A6] ${
                  selected ? "border-[#14B8A6] bg-[#E8FFFB] shadow-[inset_4px_0_0_#14B8A6]" : "border-[#D8F3FF] bg-[#F8FDFF] hover:border-[#38BDF8]"
                }`}
              >
                <span className="block text-sm font-black text-[#071A2F]">{account.customerName}</span>
                <span className="mt-1 block text-xs font-bold text-[#475569]">{account.accountNumber} / {account.status}</span>
                <span className="mt-2 inline-flex rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-xs font-black text-[#0A2540]">
                  {formatMoney(account.currentBalanceCents)}
                </span>
              </button>
            );
          })}
          {!portal.accounts.length ? <EmptyState message="No account records are visible for this client." actionLabel="Contact support" onAction={() => onNavigate("support")} /> : null}
          {portal.accounts.length && !filteredAccounts.length ? <EmptyState message="No accounts match the current search." actionLabel="Clear search" onAction={() => setAccountSearch("")} /> : null}
        </div>
        {portal.pagination.accounts.totalPages > 1 ? <nav className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[#D8E6EB] pt-3" aria-label="Account directory pagination">
          <span className="text-xs font-bold text-[#475569]">Page {portal.pagination.accounts.page} of {portal.pagination.accounts.totalPages}</span>
          <div className="flex gap-2">
            <button type="button" disabled={!portal.pagination.accounts.hasPrevious} onClick={() => onDirectoryQueryChange({ page: portal.pagination.accounts.page - 1 })} className="min-h-10 rounded-xl border border-[#BFEAFF] bg-white px-3 text-xs font-black text-[#0A2540] disabled:opacity-45">Previous</button>
            <button type="button" disabled={!portal.pagination.accounts.hasNext} onClick={() => onDirectoryQueryChange({ page: portal.pagination.accounts.page + 1 })} className="min-h-10 rounded-xl border border-[#BFEAFF] bg-white px-3 text-xs font-black text-[#0A2540] disabled:opacity-45">Next</button>
          </div>
        </nav> : null}
    </section>;
  }

  return <section className="min-w-0 bg-[#F5FCFB]" aria-label="Full selected account workspace">
        {selectedAccount ? (
          <div className="grid gap-4">
            <header className="border-b-2 border-[#80CBC4] bg-[#EAF8F5] px-4 py-4 sm:px-5">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#0F766E]">Selected work object</p>
              <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-black tracking-[-0.03em] text-[#071A2F]">{selectedAccount.customerName} <span className="font-semibold text-[#475569]">/ {selectedAccount.accountNumber}</span></h2>
                  <p className="mt-1 text-sm font-semibold text-[#475569]">{selectedAccount.status} / {selectedAccount.placementStatus} / {formatMoney(selectedAccount.currentBalanceCents)}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2" aria-label="Account navigation and Enterprise Action Bar">
                  <button type="button" onClick={onBackToAccounts} className="min-h-11 rounded-full border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-black text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">Back to Accounts</button>
                  <button type="button" aria-pressed={activeAction === "report-payment" ? true : undefined} onClick={(event) => launchForAccount("report-payment", event.currentTarget)} className={`min-h-11 rounded-full border px-4 py-2 text-sm font-black focus:outline-none focus:ring-2 focus:ring-[#14B8A6] ${activeAction === "report-payment" ? "border-[#14B8A6] bg-[#0A2540] text-white" : "border-[#BFEAFF] bg-white text-[#0A2540] hover:border-[#38BDF8]"}`}>Report Customer Payment</button>
                  <button type="button" aria-current={activeAction === "recovery" ? "page" : undefined} aria-pressed={activeAction === "recovery" ? true : undefined} onClick={(event) => launchForAccount("recovery", event.currentTarget)} className={`min-h-11 rounded-full border px-3 py-2 text-sm font-black focus:outline-none focus:ring-2 focus:ring-[#14B8A6] ${activeAction === "recovery" ? "border-[#14B8A6] bg-[#E8FFFB] text-[#0F766E]" : "border-[#BFEAFF] bg-white text-[#0A2540]"}`}>View Recovery</button>
                </div>
              </div>
            </header>
            {activeAction ? (
              <AccountActionWorkspace portal={portal} selectedAccount={selectedAccount} selectedInvoice={selectedInvoice} target={activeAction} onLaunch={launchForAccount} onSubmitted={onSubmitted} />
            ) : (
              <>
                <section className="border border-[#CFE8E3] bg-white p-5" aria-label="Account execution overview">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#0F766E]">Execution workspace</p>
                <h3 className="mt-2 text-2xl font-black text-[#071A2F]">Account Overview</h3>
                <p className="mt-2 max-w-2xl text-sm font-semibold leading-6 text-[#475569]">Review this account's current balance and recovery state, then use an authorized action to report a payment or review supporting account evidence.</p>
                <dl className="mt-5 grid gap-4 border-t border-[#E0ECEA] pt-4 sm:grid-cols-3">
                  <div><dt className="text-xs font-black uppercase tracking-[0.14em] text-[#64748B]">Current balance</dt><dd className="mt-1 text-xl font-black text-[#071A2F]">{formatMoney(selectedAccount.currentBalanceCents)}</dd></div>
                  <div><dt className="text-xs font-black uppercase tracking-[0.14em] text-[#64748B]">Recovery status</dt><dd className="mt-1 text-xl font-black text-[#071A2F]">{selectedAccount.placementStatus}</dd></div>
                  <div><dt className="text-xs font-black uppercase tracking-[0.14em] text-[#64748B]">Last meaningful activity</dt><dd className="mt-1 text-xl font-black text-[#071A2F]">{formatDate(selectedAccountLastActivity)}</dd></div>
                </dl>
                </section>
                <section className="border border-[#D8E6EB] bg-white p-4" aria-label="Selected account evidence">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#0F766E]">Account evidence</p>
                  <div className="mt-3 grid gap-5">
                    <div aria-label="Selected account payments">
                      <h3 className="text-lg font-black text-[#071A2F]">Reported customer payments</h3>
                      <div className="mt-2 grid gap-2">
                        {selectedAccountPayments.map((payment) => <div key={payment.paymentId} className="rounded-lg border border-[#E0EAEE] bg-[#F8FDFF] p-3"><p className="text-sm font-black text-[#071A2F]">{formatMoney(payment.amountCents)} / {payment.paymentMethod}</p><p className="mt-1 text-xs font-semibold text-[#64748B]">{clientPaymentStatus(payment.verificationStatus)} / reported {formatDate(payment.reportedDate)}</p></div>)}
                        {!selectedAccountPayments.length ? <p className="text-sm font-semibold text-[#64748B]">No account-specific customer payments are currently available.</p> : null}
                      </div>
                    </div>
                    <div className="border-t border-[#E0EAEE] pt-4" aria-label="Selected account statements and documents">
                      <h3 className="text-lg font-black text-[#071A2F]">Statements and documents</h3>
                      <div className="mt-2 grid gap-2">
                        {selectedAccountStatements.map((statement) => <div key={statement.statementId} className="rounded-lg border border-[#E0EAEE] bg-[#F8FDFF] p-3"><p className="text-sm font-black text-[#071A2F]">{statement.statementNumber}</p><p className="mt-1 text-xs font-semibold text-[#64748B]">{statement.lifecycleStatus} / {statement.downloadedStatus}</p></div>)}
                        {selectedAccountDocuments.map((document) => <div key={document.documentVersionId} className="rounded-lg border border-[#E0EAEE] bg-[#F8FDFF] p-3"><p className="text-sm font-black text-[#071A2F]">{document.statementNumber} document</p><p className="mt-1 text-xs font-semibold text-[#64748B]">{document.status} / {document.mimeType}</p></div>)}
                        {!selectedAccountStatements.length && !selectedAccountDocuments.length ? <p className="text-sm font-semibold text-[#64748B]">No account-specific statements or documents are currently available.</p> : null}
                      </div>
                    </div>
                  </div>
                </section>
              </>
            )}
          </div>
        ) : (
          <EmptyState message="Select an account to view customer, balance, status, and related activity." />
        )}
  </section>;
}

function AccountPlacementPanel({
  portal,
  mode,
  onClose,
  onSubmitted,
}: {
  portal: ARClientPortalPayload;
  mode: "manual" | "upload";
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [fields, setFields] = useState({ customerNumber: "", customerName: "", accountNumber: "", originalBalance: "", currentBalance: "", currency: "USD", placementDate: new Date().toISOString().slice(0, 10), externalReference: "" });
  const [file, setFile] = useState<File | undefined>();
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const submit = async () => {
    setError(""); setStatus("Submitting placement...");
    try {
      const result = await createClientAccountPlacement({ ...fields, file: mode === "upload" ? file : undefined, idempotencyKey: `client-placement:${crypto.randomUUID()}`, clientId: portal.clients[0]?.clientId });
      setStatus(`Placement ${result.submission.submissionId.slice(0, 8)} accepted with status ${result.submission.status.toLowerCase().replaceAll("_", " ")}.`);
      onSubmitted();
    } catch (submitError) {
      setStatus(""); setError(submitError instanceof Error ? submitError.message : "Placement could not be submitted.");
    }
  };
  const downloadTemplate = () => {
    const blob = new Blob(["Customer Number,Customer Name,Account Number,Original Balance,Current Balance,Currency,Placement Date,External Reference\\r\\n"], { type: "text/csv" });
    const link = document.createElement("a"); link.href = URL.createObjectURL(blob); link.download = "account-placement-template.csv"; link.click(); URL.revokeObjectURL(link.href);
  };
  return <section className="mt-4 rounded-2xl border border-[#BFEAFF] bg-[#F8FDFF] p-4" aria-label={mode === "manual" ? "Add an Account" : "Upload Accounts"}>
    <div className="flex flex-wrap items-start justify-between gap-3"><div><p className="text-xs font-black uppercase tracking-[0.16em] text-[#0F766E]">Accounts intake</p><h3 className="mt-1 text-xl font-black text-[#071A2F]">{mode === "manual" ? "Add an Account" : "Upload Accounts"}</h3><p className="mt-1 text-sm font-semibold text-[#475569]">{mode === "manual" ? "Submit one recovery account for secure validation and placement." : "Upload CSV or XLSX account placements for validation."}</p></div><button type="button" onClick={onClose} className="min-h-10 rounded-full border border-[#BFEAFF] bg-white px-3 text-sm font-black text-[#0A2540]">Cancel</button></div>
    {mode === "manual" ? <div className="mt-4 grid gap-3 sm:grid-cols-2"><input aria-label="Customer Number" placeholder="Customer Number" value={fields.customerNumber} onChange={(event) => setFields({ ...fields, customerNumber: event.target.value })} className="rounded-xl border border-[#BFEAFF] px-3 py-2" /><input aria-label="Customer Name" placeholder="Customer Name" value={fields.customerName} onChange={(event) => setFields({ ...fields, customerName: event.target.value })} className="rounded-xl border border-[#BFEAFF] px-3 py-2" /><input aria-label="Account Number" placeholder="Account Number" value={fields.accountNumber} onChange={(event) => setFields({ ...fields, accountNumber: event.target.value })} className="rounded-xl border border-[#BFEAFF] px-3 py-2" /><input aria-label="Original Balance" placeholder="Original Balance" inputMode="decimal" value={fields.originalBalance} onChange={(event) => setFields({ ...fields, originalBalance: event.target.value })} className="rounded-xl border border-[#BFEAFF] px-3 py-2" /><input aria-label="Current Balance" placeholder="Current Balance" inputMode="decimal" value={fields.currentBalance} onChange={(event) => setFields({ ...fields, currentBalance: event.target.value })} className="rounded-xl border border-[#BFEAFF] px-3 py-2" /><input aria-label="Placement Date" type="date" value={fields.placementDate} onChange={(event) => setFields({ ...fields, placementDate: event.target.value })} className="rounded-xl border border-[#BFEAFF] px-3 py-2" /><input aria-label="External Reference" placeholder="External Reference (optional)" value={fields.externalReference} onChange={(event) => setFields({ ...fields, externalReference: event.target.value })} className="rounded-xl border border-[#BFEAFF] px-3 py-2 sm:col-span-2" /></div> : <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto]"><input aria-label="CSV or XLSX account file" type="file" accept=".csv,.xlsx" onChange={(event) => setFile(event.target.files?.[0])} className="rounded-xl border border-[#BFEAFF] bg-white px-3 py-2" /><button type="button" onClick={downloadTemplate} className="min-h-11 rounded-xl border border-[#14B8A6] bg-white px-3 text-sm font-black text-[#0F766E]">Download template</button></div>}
    {error ? <p className="mt-3 text-sm font-bold text-[#B91C1C]" role="alert">{error}</p> : null}{status ? <p className="mt-3 text-sm font-bold text-[#0F766E]" role="status">{status}</p> : null}<button type="button" onClick={submit} disabled={mode === "upload" && !file} className="mt-4 min-h-11 rounded-full bg-[#0A2540] px-5 py-2 text-sm font-black text-white disabled:opacity-50">Submit Placement</button>
  </section>;
}

function AccountActionWorkspace({
  portal,
  selectedAccount,
  selectedInvoice,
  target,
  onLaunch,
  onSubmitted,
}: {
  portal: ARClientPortalPayload;
  selectedAccount: ARClientPortalPayload["accounts"][number];
  selectedInvoice: ARClientPortalPayload["billingCenter"]["invoices"][number] | null;
  target: EnterpriseWindowTarget;
  onLaunch: (target: EnterpriseWindowTarget, trigger?: HTMLElement | null) => void;
  onSubmitted: () => void;
}) {
  const title = enterpriseWindowTitle(target);
  const openWindowWorkspace = (workspace: PortalWorkspace) => onLaunch(enterpriseWindowTargetForWorkspace(workspace));
  const content = target === "report-payment"
    ? <ClientPortalPaymentReporting portal={portal} selectedAccount={selectedAccount} onSubmitted={onSubmitted} />
      : target === "recovery"
        ? <ActivityTab portal={portal} selectedAccountId={selectedAccount.accountId} />
      : target === "customer-payments" || target === "payment-activity"
        ? <PaymentsTab portal={portal} />
        : target === "statements"
          ? <ReportsWorkspace portal={portal} onNavigate={openWindowWorkspace} />
          : target === "billing" || target === "pay-now" || target === "receipt" || target === "billing-history"
            ? <BillingTab portal={portal} billingPath="/client/portal/billing" selectedInvoiceId={selectedInvoice?.billingInvoiceId || ""} isInvoiceListRoute={false} paymentReturnStatus={null} onNavigate={openWindowWorkspace} />
            : <SupportTab portal={portal} />;
  return isReadOnlyEnterpriseTarget(target)
    ? <section className="border border-[#D8E6EB] bg-white p-4" aria-label={`${title} read-only workspace`}>{content}</section>
    : <TaskPanel title={title}>{content}</TaskPanel>;
}

function BillingTab({
  portal,
  billingPath,
  selectedInvoiceId,
  isInvoiceListRoute,
  paymentReturnStatus,
  onNavigate,
}: {
  portal: ARClientPortalPayload;
  billingPath: string;
  selectedInvoiceId: string;
  isInvoiceListRoute: boolean;
  paymentReturnStatus: string | null;
  onNavigate: (workspace: PortalWorkspace, pathSuffix?: string) => void;
}) {
  const [billingView, setBillingView] = useState<"dashboard" | "invoices" | "payments" | "receipts" | "history" | "settings" | "detail" | "explanation" | "preferences" | "autopay" | "standing">("dashboard");
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("");
  const [payNowWorkingInvoiceId, setPayNowWorkingInvoiceId] = useState("");
  const [payNowError, setPayNowError] = useState("");
  const [billingDashboard, setBillingDashboard] = useState<ARClientBillingDashboard | null>(null);
  const [billingDashboardError, setBillingDashboardError] = useState("");
  const [billingDashboardLoading, setBillingDashboardLoading] = useState(true);
  const hasSelectedInvoiceRoute = Boolean(selectedInvoiceId);
  const routeBillingView = billingViewFromPath(billingPath);
  useEffect(() => {
    if (billingView !== "dashboard" || hasSelectedInvoiceRoute) return;
    let active = true;
    setBillingDashboardLoading(true);
    setBillingDashboardError("");
    getClientBillingDashboard()
      .then((payload) => { if (active) setBillingDashboard(payload); })
      .catch((error) => { if (active) setBillingDashboardError(error instanceof Error ? error.message : "Billing Dashboard could not be loaded."); })
      .finally(() => { if (active) setBillingDashboardLoading(false); });
    return () => { active = false; };
  }, [billingView, hasSelectedInvoiceRoute]);
  const paymentsByInvoice = new Map(portal.billingCenter.payments.map((payment) => [payment.billingInvoiceId, payment]));
  const receiptsByInvoice = new Map(portal.billingCenter.receipts.map((receipt) => [receipt.billingInvoiceId, receipt]));
  const billingExperience = portal.billingCenter.experience;
  const historyByInvoice = portal.billingCenter.history.reduce((map, history) => {
    const entries = map.get(history.billingInvoiceId) || [];
    entries.push(history);
    map.set(history.billingInvoiceId, entries);
    return map;
  }, new Map<string, ARClientPortalPayload["billingCenter"]["history"]>());
  const filteredInvoices = portal.billingCenter.invoices.filter((invoice) => {
    const friendlyStatus = clientInvoiceStatus(invoice);
    const haystack = [invoice.invoiceNumber, invoice.settlementNumber, friendlyStatus, formatMoney(invoice.remainingBalanceCents)].join(" ").toLowerCase();
    if (invoiceSearch && !haystack.includes(invoiceSearch.toLowerCase())) return false;
    if (invoiceStatus && friendlyStatus !== invoiceStatus) return false;
    return true;
  });
  const selectedInvoice = filteredInvoices.find((invoice) => invoice.billingInvoiceId === selectedInvoiceId)
    || portal.billingCenter.invoices.find((invoice) => invoice.billingInvoiceId === selectedInvoiceId)
    || filteredInvoices[0]
    || portal.billingCenter.invoices[0]
    || null;
  const selectedPayment = selectedInvoice ? paymentsByInvoice.get(selectedInvoice.billingInvoiceId) : null;
  const selectedReceipt = selectedInvoice ? receiptsByInvoice.get(selectedInvoice.billingInvoiceId) : null;
  const selectedInvoiceExists = !hasSelectedInvoiceRoute || portal.billingCenter.invoices.some((invoice) => invoice.billingInvoiceId === selectedInvoiceId);
  useEffect(() => {
    if (hasSelectedInvoiceRoute) setBillingView("detail");
    else if (routeBillingView) setBillingView(routeBillingView);
    else if (isInvoiceListRoute) setBillingView("invoices");
  }, [hasSelectedInvoiceRoute, isInvoiceListRoute, routeBillingView, selectedInvoiceId]);
  const billingViews: Array<{ key: "dashboard" | "invoices" | "payments" | "receipts" | "history"; label: string }> = [
    { key: "dashboard", label: "Dashboard" },
    { key: "invoices", label: "Invoices" },
    { key: "payments", label: "Payments to CollabRecover" },
    { key: "receipts", label: "Receipts" },
    { key: "history", label: "Billing History" },
  ];
  const activeBillingWorkspace = billingView === "detail" || billingView === "explanation"
    ? "invoices"
    : billingView === "preferences" || billingView === "autopay"
      ? "settings"
      : billingView === "standing"
        ? "dashboard"
        : billingView;
  const selectBillingWorkspace = (view: "dashboard" | "invoices" | "payments" | "receipts" | "history") => {
    onNavigate("billing", view === "dashboard" ? "billing" : `billing/${view}`);
  };
  const startPayNow = async (invoice: ARClientPortalPayload["billingCenter"]["invoices"][number]) => {
    setPayNowError("");
    setPayNowWorkingInvoiceId(invoice.billingInvoiceId);
    try {
      const result = await createBillingPayNowUrl(invoice.billingInvoiceId, portal.companyId);
      window.location.assign(result.paymentUrl);
    } catch (error) {
      setPayNowError(error instanceof Error ? error.message : "Online payment is temporarily unavailable. Contact CollabRecover Billing for assistance.");
    } finally {
      setPayNowWorkingInvoiceId("");
    }
  };

  return (
    <PortalCard title={activeBillingWorkspace === "dashboard" ? "Billing Overview" : billingViews.find((view) => view.key === activeBillingWorkspace)?.label || "Invoices"} eyebrow="Company-wide CollabRecover billing" contentClassName={hasSelectedInvoiceRoute ? "mt-4 -mx-5 sm:mx-0" : "mt-4"}>
      <div className="mb-4 hidden flex-wrap gap-2 lg:flex" role="tablist" aria-label="Client billing sections">
        {billingViews.map((view) => (
          <button
            key={view.key}
            type="button"
            role="tab"
            aria-selected={activeBillingWorkspace === view.key}
            onClick={() => selectBillingWorkspace(view.key)}
            className={`rounded-full border px-3 py-2 text-sm font-extrabold transition focus:outline-none focus:ring-2 focus:ring-[#14B8A6] ${
              activeBillingWorkspace === view.key ? "border-[#14B8A6] bg-[#E8FFFB] text-[#0F766E]" : "border-[#BFEAFF] bg-white text-[#0A2540] hover:border-[#38BDF8]"
            }`}
          >
            {view.label}
          </button>
        ))}
      </div>
      <div className="mb-4 lg:hidden">
        <label htmlFor="client-billing-section" className="mb-1 block text-xs font-black uppercase tracking-[0.14em] text-[#0F766E]">Billing section</label>
        <select
          id="client-billing-section"
          value={activeBillingWorkspace}
          onChange={(event) => selectBillingWorkspace(event.target.value as "dashboard" | "invoices" | "payments" | "receipts" | "history")}
          className="w-full rounded-2xl border border-[#BFEAFF] bg-white px-3 py-3 text-sm font-extrabold text-[#071A2F] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
        >
          {billingViews.map((view) => <option key={view.key} value={view.key}>{view.label}</option>)}
        </select>
      </div>
      {billingView === "dashboard" ? (
        <section className="mb-4" aria-label="Billing Dashboard">
          {billingDashboardLoading ? <div className="rounded-2xl border border-[#D8F3FF] bg-[#F8FDFF] p-5 text-sm font-semibold text-[#475569]" role="status">Loading your current billing position...</div> : null}
          {billingDashboardError ? <div className="rounded-2xl border border-[#FECACA] bg-[#FFF7F7] p-5 text-sm font-semibold text-[#991B1B]" role="alert">{billingDashboardError}</div> : null}
          {billingDashboard ? <>
            <div className="grid gap-3 lg:grid-cols-[1.25fr_.75fr]">
              <ClientBillingPanel title="Billing Position">
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  <MiniMetric label="Current standing" value={billingDashboard.summary.currentStanding} />
                  <MiniMetric label="Outstanding balance" value={formatMoney(billingDashboard.summary.totalOutstandingBalance)} />
                  <MiniMetric label="Invoices requiring payment" value={String(billingDashboard.summary.unpaidInvoiceCount)} />
                  <MiniMetric label="Overdue invoices" value={String(billingDashboard.summary.overdueInvoiceCount)} />
                  <MiniMetric label="Due in seven days" value={String(billingDashboard.summary.dueSoonInvoiceCount)} />
                </div>
              </ClientBillingPanel>
              <ClientBillingPanel title="Next Step">
                <p className="text-sm font-semibold leading-6 text-[#475569]">Review invoice details and securely pay any approved balance from the Invoices workspace.</p>
                <button type="button" onClick={() => selectBillingWorkspace("invoices")} className="mt-4 rounded-full border border-[#14B8A6] bg-[#E8FFFB] px-3 py-2 text-sm font-black text-[#0F766E] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">View invoices</button>
              </ClientBillingPanel>
            </div>
            <div className="mt-3 grid gap-3 lg:grid-cols-3">
              <ClientBillingPanel title="Recent Payments to CollabRecover">
                {billingDashboard.recentPayments.length ? billingDashboard.recentPayments.map((payment) => <MiniMetric key={payment.paymentId} label={payment.invoiceNumber || "CollabRecover payment"} value={`${formatMoney(payment.amountCents)} / ${formatDate(payment.occurredAt)}`} />) : <EmptyState message="No recent payments are available." />}
              </ClientBillingPanel>
              <ClientBillingPanel title="Recent Receipts">
                {billingDashboard.recentReceipts.length ? billingDashboard.recentReceipts.map((receipt) => <MiniMetric key={receipt.receiptId} label={receipt.receiptNumber || receipt.invoiceNumber} value={`${formatMoney(receipt.amountPaidCents)} / ${formatDate(receipt.issuedAt)}`} />) : <EmptyState message="No recent receipts are available." />}
              </ClientBillingPanel>
              <ClientBillingPanel title="Recent Billing Activity">
                {billingDashboard.recentActivity.length ? billingDashboard.recentActivity.map((activity) => <MiniMetric key={activity.historyId} label={activity.title || activity.invoiceNumber} value={formatDate(activity.occurredAt)} />) : <EmptyState message="No recent billing activity is available." />}
              </ClientBillingPanel>
            </div>
          </> : null}
        </section>
      ) : null}

      {billingView === "invoices" ? (
        <div className="mb-4 rounded-2xl border border-[#D8F3FF] bg-white p-4">
          <div className="grid gap-3 md:grid-cols-3">
            <input className="rounded-2xl border border-[#BFEAFF] px-3 py-2 text-sm font-bold text-[#071A2F] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]" value={invoiceSearch} onChange={(event) => setInvoiceSearch(event.target.value)} placeholder="Search invoice, amount, status..." aria-label="Search billing invoices" />
            <select className="rounded-2xl border border-[#BFEAFF] px-3 py-2 text-sm font-bold text-[#071A2F] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]" value={invoiceStatus} onChange={(event) => setInvoiceStatus(event.target.value)} aria-label="Filter billing invoices">
              <option value="">All client statuses</option>
              {Array.from(new Set(portal.billingCenter.invoices.map(clientInvoiceStatus))).map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
            <MiniMetric label="Visible invoices" value={String(filteredInvoices.length)} />
          </div>
        </div>
      ) : null}

      {hasSelectedInvoiceRoute && !selectedInvoiceExists ? (
        <EmptyState message="The requested invoice is unavailable." actionLabel="Back to invoices" onAction={() => onNavigate("billing", "billing/invoices")} />
      ) : null}

      {paymentReturnStatus === "success" ? (
        <div className="mb-4 rounded-2xl border border-[#99F6E4] bg-[#F0FDFA] p-4 text-sm font-semibold leading-6 text-[#115E59]" role="status">
          Payment received. CollabRecover will update this invoice after Stripe webhook confirmation. Refresh this invoice to view the authoritative paid status and receipt.
        </div>
      ) : paymentReturnStatus === "cancelled" ? (
        <div className="mb-4 rounded-2xl border border-[#BFEAFF] bg-[#F8FDFF] p-4 text-sm font-semibold leading-6 text-[#475569]" role="status">
          Online payment was cancelled. This invoice remains unpaid and is still available for secure payment.
        </div>
      ) : null}

      {billingView === "explanation" && selectedInvoice ? (
        <div className="mb-4 rounded-2xl border border-[#D8F3FF] bg-white p-4">
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">Billing Explanation</p>
          <div className="mt-3 grid gap-3 lg:grid-cols-3">
            <MiniMetric label="Why you were billed" value={billingWhy(selectedInvoice, billingExperience)} />
            <MiniMetric label="Recovery amount" value={formatMoney(selectedInvoice.originalTotalCents)} />
            <MiniMetric label="CollabRecover fee" value={displayPolicyValue(billingExperience?.policy?.feeModel, "percentageRate", "Policy rate")} />
            <MiniMetric label="Invoice date" value={formatDate(selectedInvoice.invoiceDate)} />
            <MiniMetric label="Due date" value={formatDate(selectedInvoice.dueDate)} />
            <MiniMetric label="Terms" value={displayPolicyValue(billingExperience?.policy?.paymentTerms, "term", selectedInvoice.paymentTerms)} />
            <MiniMetric label="Weekend / holiday handling" value={displayPolicyValue(billingExperience?.policy?.paymentTerms, "dayType", "Agreement terms")} />
            <MiniMetric label="Contact" value={billingExperience?.dashboard.billingContact.primary || "Contact Billing"} />
          </div>
        </div>
      ) : null}

      <div className="grid gap-3">
        {(billingView === "detail"
          ? (selectedInvoiceExists && selectedInvoice ? [selectedInvoice] : [])
          : billingView === "dashboard"
            ? []
            : billingView === "payments"
              ? filteredInvoices.filter((invoice) => paymentsByInvoice.has(invoice.billingInvoiceId))
              : filteredInvoices).map((invoice) => {
          const payment = paymentsByInvoice.get(invoice.billingInvoiceId);
          const receipt = receiptsByInvoice.get(invoice.billingInvoiceId);
          const history = historyByInvoice.get(invoice.billingInvoiceId) || [];
          const showFullDetail = billingView === "detail";
          const showPaymentsOnly = billingView === "payments";
          const showReceiptsOnly = billingView === "receipts";
          const showHistoryOnly = billingView === "history";
          if (showReceiptsOnly && !receipt) return null;
          if (showHistoryOnly && !history.length) return null;
          return (
            <article key={invoice.billingInvoiceId} aria-label={billingView === "detail" ? "Selected Invoice" : "Invoice directory item"} className="rounded-2xl border border-[#D8F3FF] bg-[#F8FDFF] p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">{billingView === "detail" ? "Selected Invoice" : "Invoice Directory"}</p>
                  <h3 className="mt-1 text-lg font-black text-[#071A2F]">{invoice.invoiceNumber}</h3>
                  <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
                    {clientInvoiceStatus(invoice)} / issued {formatDate(invoice.invoiceDate)} / due {formatDate(invoice.dueDate)}
                  </p>
                </div>
                <StatusPill label={clientInvoiceStatus(invoice)} />
              </div>

              {billingView !== "detail" ? (
                <button type="button" onClick={() => onNavigate("billing", `billing/invoices/${encodeURIComponent(invoice.billingInvoiceId)}`)} className="mt-3 rounded-full border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-black text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">
                  View invoice
                </button>
              ) : (
                <button type="button" onClick={() => onNavigate("billing", "billing/invoices")} className="mt-3 rounded-full border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-black text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">
                  Back to invoices
                </button>
              )}

              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <MiniMetric label="Original amount" value={formatMoney(invoice.originalTotalCents)} />
                <MiniMetric label="Amount paid" value={formatMoney(invoice.amountPaidCents)} />
                <MiniMetric label="Remaining balance" value={formatMoney(invoice.remainingBalanceCents)} />
              </div>

              {billingView !== "invoices" && (showFullDetail || showPaymentsOnly || billingView === "receipts") ? <div className="mt-4 grid gap-3 lg:grid-cols-2">
                <div className="rounded-2xl border border-[#D8F3FF] bg-white p-4">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">Payment History</p>
                  {payment ? (
                    <div className="mt-3 text-sm font-semibold leading-6 text-[#475569]">
                      <p className="font-black text-[#071A2F]">{formatMoney(payment.amountCents)} payment recorded</p>
                      <p>{labelStatus(payment.paymentStatus)} / {formatDate(payment.occurredAt)}</p>
                      <p>{payment.paymentMethodSummary}</p>
                    </div>
                  ) : (
                    <EmptyState message="No payment has been recorded for this invoice." />
                  )}
                </div>

                <div className="rounded-2xl border border-[#D8F3FF] bg-white p-4">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">Receipt</p>
                  {receipt ? (
                    <div className="mt-3 grid gap-3 text-sm font-semibold leading-6 text-[#475569]">
                      <div>
                        <p className="font-black text-[#071A2F]">{receipt.receiptNumber}</p>
                        <p>{formatMoney(receipt.amountPaidCents)} / issued {formatDate(receipt.issuedAt)}</p>
                        <p>{receipt.paymentMethodSummary}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <ClientActionLink href={receiptUrl(receipt.receiptId, portal.companyId)} label="View Receipt" />
                        <ClientActionLink href={receiptUrl(receipt.receiptId, portal.companyId, true)} label="Download Receipt" download />
                      </div>
                    </div>
                  ) : (
                    <EmptyState message="No receipt is available for this invoice." />
                  )}
                </div>
              </div> : null}

              {billingView !== "invoices" && (showFullDetail || billingView === "explanation") ? <div className="mt-4 rounded-2xl border border-[#D8F3FF] bg-white p-4">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">Billing Explanation</p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <MiniMetric label="Why billed" value={billingWhy(invoice, billingExperience)} />
                  <MiniMetric label="Fee model" value={displayPolicyValue(billingExperience?.policy?.feeModel, "model", "Recovery fee")} />
                  <MiniMetric label="Payment terms" value={displayPolicyValue(billingExperience?.policy?.paymentTerms, "term", invoice.paymentTerms)} />
                </div>
              </div> : null}

              {billingView !== "invoices" && (showFullDetail || showHistoryOnly) ? <div className="mt-4 rounded-2xl border border-[#D8F3FF] bg-white p-4">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">Billing History</p>
                <div className="mt-3 grid gap-3">
                  {history.map((entry) => (
                    <article key={entry.historyId} className="rounded-2xl border border-[#D8F3FF] bg-[#F8FDFF] p-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h4 className="text-sm font-black text-[#071A2F]">{clientHistoryTitle(entry.title, entry.eventType)}</h4>
                          <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">{entry.summary}</p>
                          <p className="mt-1 text-xs font-bold text-[#64748B]">Invoice {entry.invoiceNumber}</p>
                        </div>
                        <span className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-xs font-black text-[#0A2540]">
                          {formatDateTime(entry.occurredAt)}
                        </span>
                      </div>
                    </article>
                  ))}
                  {!history.length ? <EmptyState message="No client-safe billing history is available for this invoice." /> : null}
                </div>
              </div> : null}

              {billingView === "detail" ? <div className="mt-4 flex flex-wrap gap-2">
                {invoice.payNowAvailable ? invoice.payNowUrl ? (
                  <ClientActionLink href={invoice.payNowUrl} label="Pay Now" />
                ) : (
                  <button
                    type="button"
                    onClick={() => void startPayNow(invoice)}
                    disabled={payNowWorkingInvoiceId === invoice.billingInvoiceId}
                    className="inline-flex min-h-11 items-center justify-center rounded-full bg-[#0F766E] px-4 py-2 text-sm font-black text-white shadow-sm transition hover:bg-[#115E59] focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:ring-offset-2 disabled:cursor-wait disabled:opacity-70"
                  >
                    {payNowWorkingInvoiceId === invoice.billingInvoiceId ? "Preparing secure payment..." : "Pay Now"}
                  </button>
                ) : null}
                <ClientActionLink href={receipt ? receiptUrl(receipt.receiptId, portal.companyId) : "#"} label={receipt ? "Download Receipt" : "Receipt Unavailable"} download={Boolean(receipt)} />
                <button
                  type="button"
                  onClick={() => onNavigate("customer-payments")}
                  className="inline-flex items-center gap-2 rounded-full border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-extrabold text-[#0A2540] transition hover:border-[#38BDF8] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
                >
                  View Billing Payments
                </button>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 rounded-full border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-extrabold text-[#0A2540] transition hover:border-[#38BDF8] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
                >
                  Print
                </button>
              </div> : null}
              {payNowError ? <p className="mt-3 text-sm font-bold text-[#B91C1C]" role="alert">Online payment is temporarily unavailable. Contact CollabRecover Billing for assistance.</p> : null}
            </article>
          );
        })}
        {billingView === "payments" && !portal.billingCenter.payments.length ? <EmptyState message="No payment records are available." /> : null}
        {!portal.billingCenter.invoices.length ? <EmptyState message="No final CollabRecover billing invoices are available." /> : null}
      </div>
    </PortalCard>
  );
}

function PaymentsTab({
  portal,
}: {
  portal: ARClientPortalPayload;
}) {
  return (
    <div className="grid gap-4">
      <PortalCard title="Recent Reported Payments" eyebrow="Company-wide customer-payment reporting activity">
        <div className="grid gap-3">
          {portal.payments.slice(0, 12).map((payment) => (
            <RecordRow
              key={payment.paymentId}
              title={`${payment.customerName} / ${payment.accountNumber}`}
              detail={`payment ${formatDate(payment.paymentDate)} / reported ${formatDate(payment.reportedDate)} / ${payment.verificationStatus} / ${payment.paymentReference ? `reference ${payment.paymentReference}` : "reference unavailable"}`}
              meta={formatMoney(payment.amountCents)}
            />
          ))}
          {!portal.payments.length ? <EmptyState message="No customer payment reports are visible yet. Reported payments will appear here after CollabRecover receives them for review." /> : null}
        </div>
      </PortalCard>
    </div>
  );
}

function ClientPortalPaymentReporting({
  portal,
  selectedAccount,
  onSubmitted,
}: {
  portal: ARClientPortalPayload;
  selectedAccount: ARClientPortalPayload["accounts"][number] | null;
  onSubmitted: () => void;
}) {
  const [accountId, setAccountId] = useState(selectedAccount?.accountId || portal.accounts[0]?.accountId || "");
  const account = portal.accounts.find((item) => item.accountId === accountId) || portal.accounts[0] || null;
  if (!account) return <EmptyState message="No eligible client account is available for payment reporting." />;
  return (
    <div className="grid gap-4">
      <section className="rounded-2xl border border-[#D8F3FF] bg-[#F8FDFF] p-4">
        {selectedAccount ? (
          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-[#0F766E]">Authorized account scope</p>
            <p className="mt-1 text-xs font-semibold text-[#475569]">This payment report stays scoped to the selected authorized account.</p>
          </div>
        ) : (
          <label className="grid gap-1.5 text-xs font-black uppercase tracking-[0.14em] text-[#0F766E]">
            Customer account
            <select value={account.accountId} onChange={(event) => setAccountId(event.target.value)} className="min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-bold normal-case tracking-normal text-[#071A2F] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">
              {portal.accounts.map((item) => <option key={item.accountId} value={item.accountId}>{item.customerName} / {item.accountNumber}</option>)}
            </select>
          </label>
        )}
        <p className="mt-2 text-xs font-semibold leading-5 text-[#475569]">Payment reports are submitted for CollabRecover review. Client-reported allocation instructions do not override internal verification.</p>
      </section>
      <ARRecoveryClientPaymentReporting
        key={account.accountId}
        defaultClientId={account.clientId}
        defaultCustomerId={account.customerId}
        defaultOrganization={portal.clients[0]?.displayName || "Authorized client"}
        defaultAccountId={account.accountId}
        defaultAccountNumber={account.accountNumber}
        defaultCustomerName={account.customerName}
        defaultCustomerNumber={account.customerNumber}
        lockAccountContext
        readOnly={portal.membership.permissions.readOnly}
        onSubmitted={onSubmitted}
      />
    </div>
  );
}

function SettlementsTab({ portal }: { portal: ARClientPortalPayload }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <PortalCard title="Settlement Invoices" eyebrow="Client settlements">
        <div className="grid gap-3">
          {portal.settlementBilling.invoices.map((invoice) => (
            <RecordRow
              key={invoice.invoiceDraftId}
              title={invoice.invoiceNumber}
              detail={`${invoice.settlementNumber} / ${invoice.invoiceStatus} / ${invoice.paymentStatus}`}
              meta={formatMoney(invoice.amountDueCents)}
            />
          ))}
          {!portal.settlementBilling.invoices.length ? <EmptyState message="No settlement invoices are visible." /> : null}
        </div>
      </PortalCard>
      <PortalCard title="Settlement Statements" eyebrow="Approved documents">
        <div className="grid gap-3">
          {portal.settlementBilling.statements.map((statement) => (
            <RecordRow
              key={statement.statementVersionId}
              title={statement.statementNumber}
              detail={`${statement.settlementNumber} / version ${statement.version} / ${statement.status}`}
              meta={statement.downloadable ? "Download ready" : "Not available"}
            />
          ))}
          {!portal.settlementBilling.statements.length ? <EmptyState message="No settlement statements are visible." /> : null}
        </div>
      </PortalCard>
    </div>
  );
}

function DocumentsTab({ portal }: { portal: ARClientPortalPayload }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <PortalCard title="Statements" eyebrow="Statement history">
        <div className="grid gap-3">
          {portal.statements.slice(0, 12).map((statement) => (
            <RecordRow
              key={statement.statementId}
              title={statement.statementNumber}
              detail={`${statement.customerName} / ${statement.deliveryStatus} / recipient ${statement.recipientEmail}`}
              meta={formatMoney(statement.balanceCents)}
            />
          ))}
          {!portal.statements.length ? <EmptyState message="No statements are visible." /> : null}
        </div>
      </PortalCard>
      <PortalCard title="Document Versions" eyebrow="Client-safe documents">
        <div className="grid gap-3">
          {portal.documents.map((document) => (
            <RecordRow
              key={document.documentVersionId}
              title={document.statementNumber}
              detail={`${document.status} / version ${document.versionNumber} / ${document.available ? "available" : "not available"}`}
              meta={formatDate(document.createdAt)}
            />
          ))}
          {!portal.documents.length ? <EmptyState message="No statement documents are available." /> : null}
        </div>
      </PortalCard>
    </div>
  );
}

function ReportsWorkspace({
  portal,
  onNavigate,
}: {
  portal: ARClientPortalPayload;
  onNavigate: (workspace: PortalWorkspace, pathSuffix?: string) => void;
}) {
  const [reportsView, setReportsView] = useState<"reports" | "statements" | "documents" | "settlements">("reports");
  const secondaryViews: Array<{ key: typeof reportsView; label: string }> = [
    { key: "reports", label: "Reports" },
    { key: "statements", label: "Statements" },
    { key: "documents", label: "Documents" },
    { key: "settlements", label: "Recovery Settlements" },
  ];
  return (
    <div className="grid gap-4">
      <nav className="flex flex-wrap gap-2 rounded-2xl border border-[#D8F3FF] bg-[#F8FDFF] p-2" aria-label="Reports secondary navigation">
        {secondaryViews.map((view) => (
          <button
            key={view.key}
            type="button"
            aria-current={reportsView === view.key ? "page" : undefined}
            onClick={() => setReportsView(view.key)}
            className={`rounded-xl border px-3 py-2 text-sm font-black transition focus:outline-none focus:ring-2 focus:ring-[#14B8A6] ${
              reportsView === view.key ? "border-[#14B8A6] bg-white text-[#0F766E] shadow-[inset_0_-3px_0_#14B8A6]" : "border-transparent text-[#475569] hover:border-[#BFEAFF] hover:bg-white"
            }`}
          >
            {view.label}
          </button>
        ))}
      </nav>
      {reportsView === "reports" ? <ReportsTab portal={portal} /> : null}
      {reportsView === "statements" || reportsView === "documents" ? <DocumentsTab portal={portal} /> : null}
      {reportsView === "settlements" ? <SettlementsTab portal={portal} /> : null}
      {!portal.documents.length && !portal.settlementBilling.statements.length && reportsView === "reports" ? (
        <EmptyState message="No reports are available yet. Reports and downloads will appear after CollabRecover publishes client-safe documents." actionLabel="View recovery activity" onAction={() => onNavigate("recovery")} />
      ) : null}
    </div>
  );
}

function ReportsTab({ portal }: { portal: ARClientPortalPayload }) {
  const reports = [...portal.documents, ...portal.settlementBilling.statements];
  return (
    <PortalCard title="Reports" eyebrow="Client-safe reporting">
      <div className="grid gap-3">
        {reports.map((record) =>
          "documentVersionId" in record ? (
            <RecordRow
              key={record.documentVersionId}
              title={record.statementNumber}
              detail={`${record.status} / ${record.mimeType} / ${record.available ? "available" : "not available"}`}
              meta={formatDate(record.createdAt)}
            />
          ) : (
            <RecordRow
              key={record.statementVersionId}
              title={record.fileName || record.statementNumber}
              detail={`${record.settlementNumber} / ${record.status} / version ${record.version}`}
              meta={record.downloadable ? "Download ready" : "Not available"}
            />
          ),
        )}
        {!reports.length ? <EmptyState message="No reports are available for download." /> : null}
      </div>
    </PortalCard>
  );
}

function ActivityTab({
  portal,
  selectedAccountId,
}: {
  portal: ARClientPortalPayload;
  selectedAccountId?: string;
}) {
  const events = dedupeRecoveryEvents(
    [...portal.timeline, ...portal.activity]
      .filter((event) => !selectedAccountId || event.accountId === selectedAccountId)
      .sort((left, right) => new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime()),
  );
  return (
    <PortalCard title="Recovery Activity" eyebrow="Client-visible timeline">
      <div className="grid gap-3">
        {events.map((event) => (
          <RecordRow
            key={`${event.kind}-${event.eventId}`}
            title={clientRecoveryEventTitle(event)}
            detail={clientRecoveryEventSummary(event)}
            meta={formatDate(event.occurredAt)}
          />
        ))}
        {!events.length ? <EmptyState message="No client-visible recovery activity is available yet. Updates from CollabRecover will appear here after account work is published for client visibility." /> : null}
      </div>
    </PortalCard>
  );
}

function SupportTab(_props: { portal: ARClientPortalPayload }) {
  return (
    <div className="grid gap-4">
      <PortalCard title="Support" eyebrow="Client help">
        <div className="grid gap-3 text-sm font-semibold leading-6 text-[#475569]">
          <p>For account questions, payment-reporting help, billing questions, or portal access support, contact the CollabRecover support team.</p>
          <p className="rounded-2xl border border-[#D8F3FF] bg-[#F8FDFF] p-4 font-black text-[#071A2F]">support@collabrecover.com</p>
        </div>
      </PortalCard>
    </div>
  );
}

function ProfileTab({ portal }: { portal: ARClientPortalPayload }) {
  const client = portal.clients[0];
  return (
    <PortalCard title="Profile" eyebrow="Company access">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Company" value={client?.displayName || "Authorized client"} note="Current company context" />
        <Metric label="Role" value={portal.membership.permissionProfile} note={portal.membership.roleKey} />
        <Metric label="Portal Access" value={portal.membership.permissions.readOnly ? "Read Only" : "Operational"} note="Resolved permissions" />
        <Metric label="Generated" value={formatDate(portal.generatedAt)} note="Session data refreshed" />
      </div>
    </PortalCard>
  );
}

function OrganizationTab({ portal }: { portal: ARClientPortalPayload }) {
  const client = portal.clients[0];
  return (
    <PortalCard title="Organization" eyebrow="Authorized organization context">
      <div className="grid gap-3 text-sm font-semibold leading-6 text-[#475569] sm:grid-cols-2">
        <div className="rounded-2xl border border-[#D8F3FF] bg-[#F8FDFF] p-4">
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">Company</p>
          <p className="mt-1 text-base font-black text-[#071A2F]">{client?.displayName || "Authorized client"}</p>
        </div>
        <div className="rounded-2xl border border-[#D8F3FF] bg-[#F8FDFF] p-4">
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">Access profile</p>
          <p className="mt-1 text-base font-black text-[#071A2F]">{portal.membership.permissionProfile}</p>
        </div>
      </div>
      <p className="mt-4 rounded-2xl border border-[#FDE68A] bg-[#FFFBEB] p-4 text-sm font-semibold leading-6 text-[#854D0E]">
        Organization editing is not available in this release. Administrative changes remain permission-gated outside the Client Portal.
      </p>
    </PortalCard>
  );
}

function WorkspaceHeader({ title, description, action }: { title: string; description: string; action: ReactNode }) {
  return (
    <header className="rounded-[1.35rem] border border-[#D8F3FF] bg-[#F8FDFF] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">Active workspace</p>
          <h2 className="mt-1 text-2xl font-black tracking-[-0.03em] text-[#071A2F]">{title}</h2>
          <p className="mt-1 max-w-3xl text-sm font-semibold leading-6 text-[#475569]">{description}</p>
        </div>
        {action}
      </div>
    </header>
  );
}

function CompactClientContext({
  clientName,
  accountCount,
  recoveredTotal,
  billedTotal,
  standing,
  billingContact,
  lastUpdate,
}: {
  clientName: string;
  accountCount: number;
  recoveredTotal: number;
  billedTotal: number;
  standing: string;
  billingContact: string;
  lastUpdate: string;
}) {
  return (
    <section className="rounded-[1.35rem] border border-[#BFEAFF] bg-white/92 p-3 shadow-[0_12px_36px_rgba(10,37,64,.06)]" aria-label="Client context">
      <div className="grid gap-3 md:grid-cols-[1.2fr_repeat(5,minmax(0,1fr))]">
        <MiniMetric label="Client" value={clientName} />
        <MiniMetric label="Accounts" value={accountCount} />
        <MiniMetric label="Recovered" value={formatMoney(recoveredTotal)} />
        <MiniMetric label="Billing due" value={formatMoney(billedTotal)} />
        <MiniMetric label="Standing" value={standing} />
        <MiniMetric label="Updated" value={formatDateTime(lastUpdate)} />
      </div>
      <p className="mt-2 text-xs font-bold text-[#64748B]">Billing contact: {billingContact}</p>
    </section>
  );
}

function SelectedAccountContext({
  account,
  onNavigate,
}: {
  account: ARClientPortalPayload["accounts"][number];
  onNavigate: (workspace: PortalWorkspace, pathSuffix?: string) => void;
}) {
  return (
    <section className="mt-3 border-y border-[#B7DCE5] bg-[#F0F9FA] px-3 py-3" aria-label="Selected account context">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#0F766E]">Selected work object</p>
          <h3 className="mt-1 text-lg font-black text-[#071A2F]">{account.customerName} <span className="font-semibold text-[#475569]">/ {account.accountNumber}</span></h3>
          <p className="text-sm font-semibold text-[#475569]">{account.status} / {account.placementStatus} / {formatMoney(account.currentBalanceCents)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => onNavigate("accounts", `accounts/${encodeURIComponent(account.accountId)}`)} className="rounded-md border border-[#B7DCE5] bg-white px-3 py-2 text-sm font-black text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">
            Account detail
          </button>
          <button type="button" onClick={() => onNavigate("recovery")} className="rounded-md border border-[#B7DCE5] bg-white px-3 py-2 text-sm font-black text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">
            Recovery activity
          </button>
          <button type="button" onClick={() => onNavigate("reports")} className="rounded-md border border-[#B7DCE5] bg-white px-3 py-2 text-sm font-black text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">
            Statements and documents
          </button>
        </div>
      </div>
    </section>
  );
}

function EnterpriseActionBar({
  activeTask,
  selectedAccount,
  selectedInvoice,
  onReportPayment,
  onNavigate,
  neutralReportAction,
}: {
  activeTask: boolean;
  selectedAccount: ARClientPortalPayload["accounts"][number] | null;
  selectedInvoice: ARClientPortalPayload["billingCenter"]["invoices"][number] | null;
  onReportPayment: () => void;
  onNavigate: (workspace: PortalWorkspace, pathSuffix?: string) => void;
  neutralReportAction?: boolean;
}) {
  if (activeTask) {
    return <span className="rounded-full border border-[#99F6E4] bg-[#F0FDFA] px-3 py-2 text-xs font-black text-[#115E59]">Task in progress</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-2" aria-label="Enterprise Action Bar">
      <button type="button" onClick={onReportPayment} className={`inline-flex min-h-11 items-center gap-2 rounded-full border px-4 py-2 text-sm font-black transition focus:outline-none focus:ring-2 focus:ring-[#14B8A6] ${neutralReportAction ? "border-[#BFEAFF] bg-white text-[#0A2540] hover:border-[#38BDF8]" : "border-[#0A2540] bg-[#0A2540] text-white shadow-[0_10px_24px_rgba(10,37,64,.18)] hover:bg-[#123B63]"}`}>
        <ReceiptText className="size-4" aria-hidden="true" />
        Report Customer Payment
      </button>
      {selectedInvoice ? <button type="button" onClick={() => onNavigate("billing", `billing/invoices/${encodeURIComponent(selectedInvoice.billingInvoiceId)}`)} className="min-h-11 rounded-full border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-black text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">View selected invoice</button> : null}
    </div>
  );
}

function ContextWorkspace({
  portal,
  selectedInvoice,
  activeTask,
  onNavigate,
}: {
  portal: ARClientPortalPayload;
  selectedInvoice: ARClientPortalPayload["billingCenter"]["invoices"][number] | null;
  activeTask: boolean;
  onNavigate: (workspace: PortalWorkspace, pathSuffix?: string) => void;
}) {
  const activity = [...portal.timeline, ...portal.activity].slice(0, 3);
  return (
    <aside className="min-w-0 self-start rounded-xl border border-[#D8E6EB] bg-[#F7FAFB] p-3" aria-label="Context Workspace">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#64748B]">Context workspace</p>
      <h3 className="mt-1 text-base font-black text-[#071A2F]">Supporting evidence</h3>
      {selectedInvoice ? (
        <div className="mt-3 rounded-lg border border-[#D8E6EB] bg-white p-3">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-[#64748B]">Selected invoice</p>
          <p className="mt-1 text-sm font-black text-[#071A2F]">{selectedInvoice.invoiceNumber}</p>
          <p className="text-xs font-semibold text-[#475569]">{clientInvoiceStatus(selectedInvoice)} / {formatMoney(selectedInvoice.remainingBalanceCents)} remaining</p>
        </div>
      ) : null}
      <div className="mt-3 border-t border-[#D8E6EB] pt-3">
        <p className="text-xs font-black uppercase tracking-[0.14em] text-[#64748B]">Recent activity</p>
        <div className="mt-2 grid gap-2">
          {activity.map((event) => <div key={`${event.kind}-${event.eventId}`} className="rounded-lg border border-[#E0EAEE] bg-white p-2.5"><p className="text-xs font-black text-[#071A2F]">{event.title}</p><p className="mt-1 text-xs font-semibold leading-5 text-[#64748B]">{event.summary}</p></div>)}
          {!activity.length ? <p className="text-xs font-semibold text-[#64748B]">No supporting activity is available.</p> : null}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => onNavigate("customer-payments")} className="min-h-10 rounded-md border border-[#D8E6EB] bg-white px-3 py-2 text-xs font-black text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">Customer payments</button>
        <button type="button" onClick={() => onNavigate("reports")} className="min-h-10 rounded-md border border-[#D8E6EB] bg-white px-3 py-2 text-xs font-black text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">Documents</button>
      </div>
      {activeTask ? <p className="mt-4 text-xs font-semibold leading-5 text-[#475569]">Supporting context remains visible while the active task owns input, validation, and submission.</p> : null}
    </aside>
  );
}

function TaskPanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border border-[#80CBC4] bg-[#F5FCFB] p-4 shadow-[0_16px_36px_rgba(15,118,110,.12)] sm:p-5" aria-label={`${title} task`}>
      <div className="mb-5 border-b border-[#CFE8E3] pb-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#0F766E]">Execution workspace</p>
          <h3 className="mt-1 text-2xl font-black tracking-[-0.03em] text-[#071A2F]">{title}</h3>
        </div>
      </div>
      {children}
    </section>
  );
}

function enterpriseWindowTitle(target: EnterpriseWindowTarget) {
  const titles: Record<EnterpriseWindowTarget, string> = {
    "report-payment": "Report Customer Payment",
    recovery: "View Recovery",
    "customer-payments": "Customer Payments",
    statements: "Statements and Documents",
    billing: "CollabRecover Billing",
    "pay-now": "Pay Now",
    receipt: "View Receipt",
    "payment-activity": "Payment Activity",
    "billing-history": "Billing History",
    support: "Contact CollabRecover",
  };
  return titles[target];
}

function enterpriseWindowTargetForWorkspace(workspace: PortalWorkspace): EnterpriseWindowTarget {
  if (workspace === "recovery") return "recovery";
  if (workspace === "customer-payments") return "customer-payments";
  if (workspace === "reports") return "statements";
  if (workspace === "billing") return "billing";
  return "support";
}

function workspaceFromPath(pathname: string): PortalWorkspace {
  const segments = pathname.split("/").filter(Boolean);
  const portalIndex = segments.findIndex((segment) => segment === "portal");
  const section = portalIndex >= 0 ? segments[portalIndex + 1] : "";
  if (section === "accounts") return "accounts";
  if (section === "recovery" || section === "activity") return "recovery";
  if (section === "payments" || section === "customer-payments") return "customer-payments";
  if (section === "billing") return "billing";
  if (section === "reports" || section === "documents" || section === "settlements") return "reports";
  if (section === "organization") return "organization";
  if (section === "support") return "support";
  if (section === "profile") return "profile";
  return "accounts";
}

function actionFromPath(pathname: string) {
  return pathname === "/client/portal/report-payment" ? "report-payment" : "none";
}

function getSafeReturnPath(search: string) {
  const returnTo = new URLSearchParams(search).get("returnTo") || "";
  return returnTo.startsWith("/client/portal") && !returnTo.startsWith("//") ? returnTo : "/client/portal";
}

function taskOriginLabel(search: string) {
  const returnTo = getSafeReturnPath(search);
  if (returnTo.includes("/payments")) return "Customer Payments";
  if (new URLSearchParams(search).get("workspaceWindow") === "recovery" || returnTo.includes("workspaceWindow=recovery")) return "Recovery";
  if (returnTo.includes("/accounts/")) return "Account Readiness";
  return "your previous workspace";
}

function enterpriseWindowTargetFromSearch(search: string): EnterpriseWindowTarget | null {
  const target = new URLSearchParams(search).get("workspaceWindow");
  return target && isEnterpriseWindowTarget(target) ? target : null;
}

function enterpriseWindowAccountId(search: string) {
  return new URLSearchParams(search).get("workspaceAccountId") || "";
}

function enterpriseWindowInvoiceId(search: string) {
  return new URLSearchParams(search).get("workspaceInvoiceId") || "";
}

function enterpriseWindowOriginPath(pathname: string, search: string) {
  const activeTarget = enterpriseWindowTargetFromSearch(search);
  if (activeTarget === "recovery") {
    const parameters = new URLSearchParams(search);
    parameters.delete("returnTo");
    const normalized = parameters.toString();
    return `${pathname}${normalized ? `?${normalized}` : ""}`;
  }
  if (activeTarget) return getSafeReturnPath(search);
  return `${pathname}${stripEnterpriseWindowSearch(search)}`;
}

function enterpriseWindowRoute({
  target,
  pathname,
  search,
  accountId,
  invoiceId,
}: {
  target: EnterpriseWindowTarget;
  pathname: string;
  search: string;
  accountId: string;
  invoiceId: string;
}) {
  const origin = enterpriseWindowOriginPath(pathname, search);
  const parameters = new URLSearchParams(stripEnterpriseWindowSearch(search));
  parameters.set("workspaceWindow", target);
  parameters.set("returnTo", origin);
  if (accountId) parameters.set("workspaceAccountId", accountId);
  if (invoiceId) parameters.set("workspaceInvoiceId", invoiceId);
  return `${pathname}?${parameters.toString()}`;
}

function stripEnterpriseWindowSearch(search: string) {
  const parameters = new URLSearchParams(search);
  parameters.delete("workspaceWindow");
  parameters.delete("workspaceAccountId");
  parameters.delete("workspaceInvoiceId");
  parameters.delete("returnTo");
  const normalized = parameters.toString();
  return normalized ? `?${normalized}` : "";
}

function isEnterpriseWindowTarget(value: string): value is EnterpriseWindowTarget {
  return [
    "report-payment",
    "recovery",
    "customer-payments",
    "statements",
    "billing",
    "pay-now",
    "receipt",
    "payment-activity",
    "billing-history",
    "support",
  ].includes(value);
}

function accountIdFromPath(pathname: string) {
  const segments = pathname.split("/").filter(Boolean);
  const accountsIndex = segments.findIndex((segment) => segment === "accounts");
  return accountsIndex >= 0 ? decodeURIComponent(segments[accountsIndex + 1] || "") : "";
}

function invoiceIdFromPath(pathname: string) {
  const segments = pathname.split("/").filter(Boolean);
  const invoicesIndex = segments.findIndex((segment) => segment === "invoices");
  return invoicesIndex >= 0 ? decodeURIComponent(segments[invoicesIndex + 1] || "") : "";
}

function billingInvoiceListFromPath(pathname: string) {
  return pathname.replace(/\/+$/, "") === "/client/portal/billing/invoices";
}

function billingViewFromPath(pathname: string): "dashboard" | "invoices" | "payments" | "receipts" | "history" | null {
  const normalized = pathname.replace(/\/+$/, "");
  if (normalized === "/client/portal/billing" || normalized === "/client/portal/billing/dashboard") return "dashboard";
  if (normalized === "/client/portal/billing/invoices") return "invoices";
  if (normalized === "/client/portal/billing/payments") return "payments";
  if (normalized === "/client/portal/billing/receipts") return "receipts";
  if (normalized === "/client/portal/billing/history") return "history";
  return null;
}

function routeForWorkspace(workspace: PortalWorkspace) {
  if (workspace === "customer-payments") return "payments";
  if (workspace === "overview") return "accounts";
  return workspace;
}

function workspacePurpose(workspace: PortalWorkspace) {
  switch (workspace) {
    case "accounts":
      return "Review customer accounts, balances, statuses, and related activity without leaving the Client Portal shell.";
    case "recovery":
      return "Track client-visible recovery updates, timeline events, and related account work.";
    case "customer-payments":
      return "Report and track payments received from your customers before CollabRecover verification.";
    case "billing":
      return "Review CollabRecover invoices, secure Stripe card payments, and client-safe receipts.";
    case "reports":
      return "Access statements, settlement reports, and client-safe document downloads.";
    case "organization":
      return "Review your authorized organization context without exposing administrative controls.";
    case "support":
      return "Get help with accounts, billing, portal access, or payment reporting.";
    case "profile":
      return "Review your company access profile and resolved portal permissions.";
    default:
      return "Use this command center to jump directly into active accounts, recovery updates, billing, reports, and support.";
  }
}

function isReadOnlyEnterpriseTarget(target: EnterpriseWindowTarget) {
  return target !== "report-payment" && target !== "pay-now";
}

function PortalFrame({ children, compact = false }: { children: ReactNode; compact?: boolean }) {
  return (
    <section className={`rounded-[1.75rem] border border-[#BFEAFF] bg-white shadow-[0_24px_80px_rgba(10,37,64,.10)] ${compact ? "p-4 sm:p-4" : "p-5 sm:p-6"}`}>
      {children}
    </section>
  );
}

function PortalCard({ title, eyebrow, children, showHeader = true, contentClassName }: { title: string; eyebrow: string; children: ReactNode; showHeader?: boolean; contentClassName?: string }) {
  return (
    <section className="min-w-0 rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_18px_60px_rgba(10,37,64,.08)]">
      {showHeader ? <>
        <p className="text-xs font-black uppercase tracking-[0.2em] text-[#14B8A6]">{eyebrow}</p>
        <h2 className="mt-2 text-2xl font-black tracking-[-0.03em] text-[#071A2F]">{title}</h2>
      </> : null}
      <div className={contentClassName ?? (showHeader ? "mt-4" : "")}>{children}</div>
    </section>
  );
}

function Metric({ label, value, note, dense = false }: { label: string; value: string | number; note: string; dense?: boolean }) {
  return (
    <div className={`rounded-2xl border border-[#D8F3FF] bg-[#F8FDFF] ${dense ? "p-3" : "p-4"}`}>
      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">{label}</p>
      <p className={`${dense ? "mt-1 text-xl" : "mt-2 text-2xl"} font-black text-[#071A2F]`}>{value}</p>
      <p className={`${dense ? "mt-0.5" : "mt-1"} text-xs font-bold text-[#64748B]`}>{note}</p>
    </div>
  );
}

function MiniMetric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-[#D8F3FF] bg-white p-3">
      <p className="text-[0.68rem] font-black uppercase tracking-[0.14em] text-[#14B8A6]">{label}</p>
      <p className="mt-1 break-words text-sm font-black text-[#071A2F]">{value || "Not set"}</p>
    </div>
  );
}

function StatusPill({ label }: { label: string }) {
  return (
    <span className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-sm font-black text-[#0A2540]">
      Status: {label}
    </span>
  );
}

function ClientActionLink({ href, label, download = false }: { href: string; label: string; download?: boolean }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      download={download || undefined}
      className="inline-flex items-center gap-2 rounded-full bg-[#0A2540] px-3 py-2 text-sm font-extrabold text-white transition hover:bg-[#123B63] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
    >
      {label}
      <ArrowRight className="size-4" aria-hidden="true" />
    </a>
  );
}

function RecordRow({
  title,
  detail,
  meta,
  action,
  actionLabel,
  onAction,
}: {
  title: string;
  detail: string;
  meta: string;
  action?: { href: string; label: string };
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <article className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#D8F3FF] bg-[#F8FDFF] p-4">
      <div className="min-w-0">
        <h3 className="text-base font-black text-[#071A2F]">{title}</h3>
        <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">{detail}</p>
      </div>
      <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto sm:flex-nowrap">
        <span className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-sm font-black text-[#0A2540]">{meta}</span>
        {action ? (
          <a
            href={action.href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#0A2540] px-3 py-2 text-sm font-extrabold text-white transition hover:bg-[#123B63] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
          >
            {action.label}
            <ArrowRight className="size-4" aria-hidden="true" />
          </a>
        ) : null}
        {onAction && actionLabel ? (
          <button
            type="button"
            onClick={onAction}
            className="inline-flex items-center gap-2 rounded-full bg-[#0A2540] px-3 py-2 text-sm font-extrabold text-white transition hover:bg-[#123B63] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
          >
            {actionLabel}
            <ArrowRight className="size-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </article>
  );
}

function EmptyState({ message, actionLabel, onAction }: { message: string; actionLabel?: string; onAction?: () => void }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#BFEAFF] bg-[#F8FDFF] p-5 text-sm font-semibold text-[#475569]">
      <Bell className="mb-2 size-5 text-[#14B8A6]" aria-hidden="true" />
      <p>{message}</p>
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          className="mt-3 rounded-full border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-black text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

function formatMoney(cents = 0) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format((Number(cents) || 0) / 100);
}

function formatDate(value: string | null | undefined) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function labelStatus(value: string) {
  return String(value || "")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase()) || "Not Set";
}

function clientPaymentStatus(value: string) {
  const normalized = String(value || "").toUpperCase();
  if (["VERIFIED", "APPROVED", "COMPLETED"].includes(normalized)) return "Verified";
  if (["REJECTED", "REVERSED", "CANCELLED"].includes(normalized)) return "Needs Information";
  if (["PAYMENT_REPORTED", "SUBMITTED", "PENDING", "NOT_REVIEWED"].includes(normalized)) return "Awaiting Review";
  return "Submitted";
}

function dedupeRecoveryEvents(events: ARClientPortalEvent[]) {
  const seen = new Set<string>();
  return events.filter((event) => {
    const key = recoveryEventKey(event);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function recoveryEventKey(event: ARClientPortalEvent) {
  if (event.correlationId) return `correlation:${event.correlationId}`;
  if (event.sourceEventId) return `source:${event.sourceEventId}`;
  return [event.accountId, event.eventType, event.occurredAt, event.summary].join(":");
}

function clientRecoveryEventTitle(event: ARClientPortalEvent) {
  const normalizedType = event.eventType.replace(/[^a-z0-9]/gi, "").toUpperCase();
  if (normalizedType === "PAYMENTREPORTED") return "Customer payment reported";
  return event.title.trim() ? labelStatus(event.title) : labelStatus(event.eventType);
}

function clientRecoveryEventSummary(event: ARClientPortalEvent) {
  const normalizedType = event.eventType.replace(/[^a-z0-9]/gi, "").toUpperCase();
  if (normalizedType === "PAYMENTREPORTED") return "A customer payment was reported for this account.";
  const safeSummary = event.summary
    .replace(/preview client payment reporting fixture\.?/gi, "")
    .replace(/synthetic/gi, "")
    .trim();
  return safeSummary || "A client-visible recovery update was recorded for this account.";
}

function clientHistoryTitle(title: string, eventType: string) {
  const safeTitle = title.trim();
  if (safeTitle) return safeTitle;
  return labelStatus(eventType.replace(/^Billing/, ""));
}

function billingWhy(
  invoice: ARClientPortalPayload["billingCenter"]["invoices"][number],
  experience: ARClientPortalPayload["billingCenter"]["experience"],
) {
  const triggers = experience?.policy?.triggerRules || [];
  const trigger = triggers[0] ? labelStatus(triggers[0]) : "approved settlement";
  return `${trigger} for ${invoice.settlementNumber || invoice.settlementId}`;
}

function displayPolicyValue(source: Record<string, unknown> | undefined, key: string, fallback: string) {
  const value = source?.[key];
  if (value == null || value === "") return fallback;
  if (Array.isArray(value)) return value.map((item) => labelStatus(String(item))).join(", ");
  return labelStatus(String(value));
}

function clientInvoiceStatus(invoice: ARClientPortalPayload["billingCenter"]["invoices"][number]) {
  if (invoice.remainingBalanceCents <= 0 || invoice.paymentStatus === "PAID") return "Paid";
  if (invoice.paymentStatus === "PARTIALLY_PAID") return "Partially Paid";
  const due = new Date(invoice.dueDate).getTime();
  const today = Date.now();
  const oneDay = 1000 * 60 * 60 * 24;
  if (!Number.isNaN(due) && due < today) return "Past Due";
  if (!Number.isNaN(due) && Math.abs(due - today) < oneDay) return "Due Today";
  if (!Number.isNaN(due) && due - today <= oneDay * 7) return "Due Soon";
  if (invoice.invoiceStatus === "VOIDED") return "Cancelled";
  if (invoice.invoiceStatus === "CREDITED") return "Credited";
  return "Open";
}

function receiptUrl(receiptId: string, companyId: string, download = false) {
  const query = new URLSearchParams({ companyId });
  if (download) query.set("download", "1");
  return `/api/ar-recovery/client-portal/receipts/${encodeURIComponent(receiptId)}?${query}`;
}

function ClientBillingPanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-[#D8F3FF] bg-white p-4">
      <p className="mb-3 text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">{title}</p>
      <div className="grid gap-2">{children}</div>
    </section>
  );
}
